export type Locale = 'en' | 'es' | 'pt' | 'de' | 'fr';

export type Translations = {
  // Centralized, mapped error copy. Surfaced via lib/errors/mapApiError +
  // the toast/banner system so users never see a raw backend dump or a
  // generic "please try again". Every message is specific and actionable.
  errors: {
    generic: {
      unknownTitle: string;
      unknownMessage: string;
      badRequestTitle: string;
      badRequestMessage: string;
      notFoundTitle: string;
      notFoundMessage: string;
      conflictTitle: string;
      conflictMessage: string;
      unsupportedFileTitle: string;
      unsupportedFileMessage: string;
      fileTooLargeTitle: string;
      fileTooLargeMessage: string;
      retry: string;
      dismiss: string;
    };
    network: {
      offlineTitle: string;
      offlineMessage: string;
      timeoutTitle: string;
      timeoutMessage: string;
      serviceUnavailableTitle: string;
      serviceUnavailableMessage: string;
      serverErrorTitle: string;
      serverErrorMessage: string;
    };
    auth: {
      sessionExpiredTitle: string;
      sessionExpiredMessage: string;
      signInCta: string;
      invalidCredentialsTitle: string;
      invalidCredentialsMessage: string;
      accountLockedTitle: string;
      accountLockedMessage: string;
      accountDeactivatedTitle: string;
      accountDeactivatedMessage: string;
      forbiddenTitle: string;
      forbiddenMessage: string;
      invalidCodeTitle: string;
      invalidCodeMessage: string;
      currentPasswordWrongTitle: string;
      currentPasswordWrongMessage: string;
      userExistsTitle: string;
      userExistsMessage: string;
    };
    credits: {
      insufficientTitle: string;
      insufficientMessage: string;
      buyCreditsCta: string;
      minAmountTitle: string;
      minAmountMessage: string;
      invalidAmountTitle: string;
      invalidAmountMessage: string;
      noPaymentMethodTitle: string;
      noPaymentMethodMessage: string;
      checkoutFailedTitle: string;
      checkoutFailedMessage: string;
      subscriptionRequiredTitle: string;
      subscriptionRequiredMessage: string;
      subscribeCta: string;
    };
    orders: {
      notFoundTitle: string;
      notFoundMessage: string;
      accessDeniedTitle: string;
      accessDeniedMessage: string;
      readOnlyTitle: string;
      readOnlyMessage: string;
      createFailedTitle: string;
      createFailedMessage: string;
    };
    corpus: {
      notFoundTitle: string;
      notFoundMessage: string;
      duplicateTermTitle: string;
      duplicateTermMessage: string;
    };
    plan: {
      limitTitle: string;
      limitMessage: string;
      upgradeCta: string;
      alreadyFreeTitle: string;
      alreadyFreeMessage: string;
    };
    processing: {
      timeoutTitle: string;
      timeoutMessage: string;
      failedTitle: string;
      failedMessage: string;
    };
  };
  formatTemplates: {
    title: string; subtitle: string; adminTitle: string; adminSubtitle: string;
    newTemplate: string; newGlobalTemplate: string; duplicateBtn: string; duplicateFromGlobalHint: string;
    editBtn: string; deleteBtn: string; confirmDeleteBtn: string; setDefaultBtn: string;
    defaultBadge: string; globalBadge: string; ownBadge: string;
    myTemplates: string; globalTemplates: string; filterAll: string; filterGlobal: string; filterAccounts: string;
    loading: string; empty: string; emptyAdmin: string; copySuffix: string; errorGeneric: string;
    summaryHeader: string; summaryNumbering: string; summaryFill: string; summaryClosing: string; yes: string; off: string;
    sectionGeneral: string; sectionPage: string; sectionTypography: string; sectionHeaderFooter: string;
    sectionNumbering: string; sectionFill: string; sectionClosing: string;
    fieldName: string; fieldDescription: string;
    fieldPageFormat: string; fieldOrientation: string; portrait: string; landscape: string; a4: string; letter: string; legal: string;
    fieldMarginTop: string; fieldMarginBottom: string; fieldMarginLeft: string; fieldMarginRight: string;
    fieldFontFamily: string; fieldFontSize: string; fieldLineHeight: string; fieldTextAlign: string;
    alignJustify: string; alignLeft: string; alignCenter: string; alignRight: string; fieldParagraphSpaceAfter: string;
    fieldHeaderEnabled: string; fieldHeaderText: string; fieldHeaderAlign: string; fieldHeaderMode: string;
    headerModeAll: string; headerModeFirst: string; headerModeRest: string; fieldHeaderFontSize: string; fieldHeaderFontSizeHint: string;
    fieldFooterEnabled: string; fieldFooterText: string;
    fieldPageNumbering: string; fieldPageNumberFormat: string; numberFormatN: string; numberFormatNdeM: string; numberFormat00n: string;
    fieldPageNumberStart: string; fieldPageNumberPosition: string; posFooterCenter: string; posFooterRight: string; posHeaderRight: string;
    fieldFillShortLines: string; fieldFillChar: string; fieldFillScope: string;
    fillScopeParagraphEnd: string; fillScopeLine: string; fillScopeBlankOnly: string;
    fieldClosingEnabled: string; fieldClosingHtml: string; fieldClosingKeepTogether: string; variablesHint: string;
    mirrorMakeEditable: string; mirrorSheetNote: string; verifiedBadge: string; alreadyInMenu: string; detectSaveDirect: string; swipeForMore: string; orderFormatLabel: string; orderFormatHint: string; previewRealLoading: string; closingLangLabel: string; closingLangEs: string; closingLangTarget: string; smallMarginWarn: string; colEmpty: string; colText: string; colPageNum: string; colTextPlaceholder: string;
    colRemove: string; colAdd: string; colSwitchToColumns: string; fieldFooterRule: string; sectionRules: string;
    fieldLineNumbering: string; fieldLinesPerPage: string; fieldLineNumberRule: string; lineNumberingHint: string;
    pinBtn: string; pinnedBadge: string; pinHint: string; addToMenuBtn: string; deleteInUseConfirm: string; deleteInUseConfirmOne: string; deleteInUseConfirmOther: string;
    searchPlaceholder: string; catAll: string; catGeneral: string; catCertified: string; catCourt: string;
    pickerNoPinned: string;
    uploadWordBtn: string; detectTitle: string; detectReading: string; detectIntro: string;
    detectNotFound: string; detectYes: string; detectNo: string; detectHint: string;
    detectOpenEditor: string; detectFailed: string;
    previewTitle: string; previewAllMatch: string; previewGuides: string; previewGuideHeader: string; previewGuideFooter: string; previewGuideContent: string; previewRuler: string; previewRulerDrag: string; previewPageOf: string; previewTabScreen: string; previewTabWord: string; previewTabPdf: string; previewSample1: string; previewSample2: string;
    previewSample3: string; previewClosingSignature: string; previewPageBreak: string; mirrorNotice: string;
    previewDocxBtn: string; previewDocxError: string;
    saveBtn: string; savingBtn: string; cancelBtn: string; createBtn: string; backBtn: string; unsavedConfirm: string; savedOk: string; unsavedTitle: string; unsavedStay: string; unsavedLeave: string; variablesLangHint: string; closingSignatureToggle: string; closingSignatureUpload: string; closingSignatureRemove: string;
    defaultCardLabel: string; defaultCardHint: string; defaultNone: string; defaultManageLink: string; defaultSaved: string;
    pickerLabel: string; pickerApplying: string; pickerError: string; pickerNone: string;
    wordOnly: string;
    noMatches: string;
    dropWordHere: string;
    dragWordOrClick: string;
    privacyNote: string;
    fieldUnit: string;
    unitCm: string;
    unitIn: string;
    ruleRepeatSeal: string;
    ruleDescribeIllegible: string;
    ruleInvalidateBack: string;
    ruleSingleSided: string;
    colImage: string;
    colImageLogo: string;
    colImageSignature: string;
    colImageSeal: string;
    colImageRemove: string;
    colImageUpload: string;
    colImageUploading: string;
    colImageTooBig: string;
    colImageUploadError: string;
    colSizeS: string;
    colSizeM: string;
    colSizeL: string;
    colSizeCustom: string;
    detectHeaderImage: string;
    detectFooterImage: string;
    sampleFileName: string;
    sampleDefaultName: string;
    errHeaderEmpty: string;
    errClosingEmpty: string;
    errNameRequired: string;
    errInvalidValue: string;
    lockedNotice: string;
    marginsBox: string;
    marginsLink: string;
    marginsLinkHint: string;
    bandToolbarLabel: string;
    blockMoveLeft: string;
    blockMoveRight: string;
    blockDelete: string;
    blockDrag: string;
    blockDragInstructions: string;
    blockTextLabel: string;
    blockTextPlaceholder: string;
    blockTextEditorHint: string;
    blockImageHeight: string;
    blockImageResize: string;
    blockImageProfile: string;
    bandEmpty: string;
    bandHeaderEmptyWarn: string;
    bandAutoPageNum: string;
    bandOrderHint: string;
    bandClickToEdit: string;
    blocksHeader: string;
    blocksFooter: string;
    bandSheetHint: string;
    blockMaxReached: string;
    blockPageNumExists: string;
    blockImageLocked: string;
    blockDeleted: string;
    blockUndo: string;
    blockImageChange: string;
    blockImageUploadNew: string;
    previewEditingMockup: string;
    previewShowReal: string;
    previewZoom: string;
    previewZoomOut: string;
    previewZoomIn: string;
    previewZoomActual: string;
    previewZoomFit: string;
    fieldHeaderDistance: string;
    fieldFooterDistance: string;
    bandDistanceHint: string;
    bandDistanceTooBig: string;
    bandHeaderPushes: string;
    bandFooterPushes: string;
    marginsTooBig: string;
    bandFieldPlaceholderHeader: string;
    bandFieldPlaceholderFooter: string;
    bandFieldHint: string;
    bandAlignLeft: string;
    bandAlignCenter: string;
    bandAlignRight: string;
    bandImage: string;
    bandImageHeightTitle: string;
    bandImageHeightField: string;
    bandImageSmaller: string;
    bandImageBigger: string;
    bandImageRemove: string;
    bandImagesMax: string;
    bandImageNotAllowed: string;
    bandPageNumber: string;
    bandColumns: string;
    bandColumns2: string;
    bandColumns3: string;
    bandColumnsRemove: string;
    bandColumnsOnlyOne: string;
    bandLinesMax: string;
    bandNumberOff: string;
    bandNumberInBand: string;
    bandNumberTwice: string;
    bandEditorFailed: string;
    bandEditorRetry: string;
    bandRichNotSaved: string;
    bandDialogApply: string;
    bandDialogCancel: string;
    bandTooLong: string;
    bandImagesTooTall: string;
    bandNumberNotAllPages: string;
    mixtoSeccion: string;
    mixtoCompleto: string;
    mixtoCompletoDesc: string;
    mixtoSoloBandas: string;
    mixtoSoloBandasDesc: string;
    mixtoNoUsa: string;
    mixtoBloqueado: string;
    mixtoSinBandas: string;
    mixtoBadge: string;
    mixtoNoGuardado: string;
    mixtoNoDisponible: string;
    mixtoD1TituloAmbos: string;
    mixtoD1TituloEncabezado: string;
    mixtoD1TituloPie: string;
    mixtoD1Intro: string;
    mixtoD1Reemplazar: string;
    mixtoD1ReemplazarDesc: string;
    mixtoD1Conservar: string;
    mixtoD1ConservarDesc: string;
    mixtoD1Provisional: string;
    mixtoD1Pregunta: string;
    mixtoD1NecesitaRetraducir: string;
    mixtoD1AvisoRegenerar: string;
    mixtoD1Aplicar: string;
    mixtoD1Despues: string;
    mixtoD1Aplicando: string;
    mixtoD1Error: string;
    mixtoChipPendiente: string;
    mixtoChipConservado: string;
    mixtoChipReemplazado: string;
    mixtoChipCambiar: string;
    mixtoD2Titulo: string;
    mixtoD2Intro: string;
    mixtoD2Fila: string;
    mixtoD2NoCabe: string;
    mixtoD2Nota: string;
    mixtoD2Entendido: string;
    mixtoChipReduccionUna: string;
    mixtoChipReduccionVarias: string;
    mixtoCerrar: string;
    mixtoD1PreguntaEncabezado: string;
    mixtoD1PreguntaPie: string;
    mixtoChipPendienteEncabezado: string;
    mixtoChipPendientePie: string;
    mixtoChipConservadoEncabezado: string;
    mixtoChipConservadoPie: string;
    mixtoChipReemplazadoEncabezado: string;
    mixtoChipReemplazadoPie: string;
    mixtoD2NotaNoCabe: string;
    mixtoCancelar: string;
    mixtoD1Continuar: string;
    mixtoD1OtroTraduccion: string;
    mixtoBandasHoja: string;
    mixtoBandasHojaDesc: string;
    mixtoNoUsaBandasHoja: string;
    mixtoPersonalizado: string;
    mixtoPersonalizadoDesc: string;
    mixtoNoUsaPersonalizado: string;
    mixtoFilas: string;
    mixtoFilaHoja: string;
    mixtoFilaOrientacion: string;
    mixtoFilaMargenes: string;
    mixtoFilaEncabezado: string;
    mixtoFilaPie: string;
    mixtoFilaTipografia: string;
    mixtoFilaTablas: string;
    mixtoFilaGuiones: string;
    mixtoFilaCierre: string;
    mixtoDeLaPlantilla: string;
    mixtoDelOriginal: string;
    mixtoPorAhoraOriginal: string;
    mixtoHojaConMargenes: string;
    mixtoOrientacionConHoja: string;
    mixtoBloqueadoFila: string;
    mixtoBloqueadoAtajo: string;
    mixtoSeccionNoSeUsa: string;
    mixtoBadgePersonalizado: string;
    mixtoVistaMuestra: string;
    mixtoD2IntroHoja: string;
    mixtoD2FilaHoja: string;
    mixtoD2NotaHoja: string;
    mixtoCombinacion: string;
    mixtoErrCoherencia: string;
    mixtoTodoOriginal: string;
    mixtoD2TituloHoja: string;
    mixtoErrSeccionDelOriginal: string;
  };
  sidebar: {
    formatTemplates: string;
    hubspot: string;
    overview: string;
    accounts: string;
    users: string;
    logs: string;
    waitlist: string;
    templates: string;
    analytics: string;
    dashboard: string;
    orders: string;
    team: string;
    credits: string;
    certificate: string;
    settings: string;
    myTasks: string;
    myWorkspace: string;
    account: string;
    activity: string;
    plans: string;
    features: string;
    signOut: string;
    closeSidebar: string;
    openSidebar: string;
    accountMenu: string;
    corpus: string;
    myTerms: string;
    companySettings: string;
    companyDataDesc: string;
    blog: string;
    landingPages: string;
    navMenu: string;
    glossary: string;
    siteCopy: string;
    seo: string;
    apiKeys: string;
    support: string;
    knowledge: string;
    eventRegistrations: string;
    content: string;
    events: string;
    payments: string;
    aiCosts: string;
    infraCosts: string;
    campaignCodes: string;
    ambassadorApplications: string;
    ambassadors: string;
    applications: string;
    invite: string;
    ambassadorAccount: string;
    adminAccount: string;
    ambassadorHome: string;
    ambassadorReferrals: string;
    ambassadorCommissions: string;
    ambassadorNetwork: string;
    asistente: string;
  };
  dashboard: {
    welcome: string;
    subtitle: string;
    newOrder: string;
    lowCredits: string;
    creditsBalance: string;
    ordersInProgress: string;
    completedOrders: string;
    teamMembers: string;
    available: string;
    low: string;
    active: string;
    recentOrders: string;
    viewAll: string;
    noOrdersYet: string;
    createFirstOrder: string;
    quickActions: string;
    uploadDocument: string;
    startNewTranslation: string;
    buyCredits: string;
    addToBalance: string;
    inviteTeam: string;
    addTeamMembers: string;
    companyOverview: string;
    thisMonth: string;
    totalOrders: string;
    inProgress: string;
    completed: string;
    recentActivity: string;
    translation: string;
    translationEdit: string;
    lastActivity: string;
    noActivityYet: string;
    pages: string;
    topLanguages: string;
    creditActivity: string;
    document: string;
    languages: string;
    date: string;
    ordersCount: string;
    archivedCount: string;
    prev: string;
    next: string;
    noTransactions: string;
    status: string;
    ocrGenerated: string;
    ocrEdit: string;
    download: string;
    txTranslationCompleted: string;
    txCreditsPurchased: string;
    txCreditsRefunded: string;
    txCreditsAdjusted: string;
    txAutoRecharge: string;
    hideHeader: string;
  };
  statuses: {
    uploaded: string;
    processing: string;
    ocrreview: string;
    translationreview: string;
    completed: string;
    cancelled: string;
    unknown: string;
  };
  notifications: {
    title: string;
    markAllRead: string;
    noNotifications: string;
    dismiss: string;
    close: string;
    orderAssignedTitle: string;
    orderAssignedMessage: string;
    orderCompletedTitle: string;
    orderCompletedMessage: string;
    orderReassignedTitle: string;
    orderReassignedMessage: string;
    projectCompletedTitle: string;
    projectCompletedMessage: string;
    finalizationRequestedTitle: string;
    finalizationRequestedMessage: string;
    finalizationReturnedTitle: string;
    finalizationReturnedMessage: string;
  };
  time: {
    justNow: string;
    minAgo: string;
    hourAgo: string;
    hoursAgo: string;
    yesterday: string;
    daysAgo: string;
    mAgo: string;
    hAgo: string;
    dAgo: string;
  };
  orders: {
    title: string;
    subtitle: string;
    newOrder: string;
    total: string;
    uploaded: string;
    inProgress: string;
    completed: string;
    thisMonth: string;
    noOrdersFound: string;
    tryAdjusting: string;
    createFirst: string;
    order: string;
    document: string;
    type: string;
    pagesCol: string;
    creditsCol: string;
    qualityCol: string;
    languageCol: string;
    status: string;
    createdBy: string;
    assignedTo: string;
    client: string;
    date: string;
    actions: string;
    showing: string;
    of: string;
    image: string;
    text: string;
    handwritten: string;
    modeTranslate: string;
    modeLayout: string;
    searchPlaceholder: string;
    allStatus: string;
    allTypes: string;
    allDocTypes: string;
    allWorkTypes: string;
    view: string;
    clearDates: string;
    verifiedBy: string;
    due: string;
    open: string;
    filterProjects: string;
    filterSingle: string;
    archived: string;
    toSetUp: string;
    cancelled: string;
    completedOf: string;
    moveToProject: string;
    removeFromProject: string;
    notAssigned: string;
    trash: string;
    trashAction: string;
    trashConfirmTitle: string;
    trashConfirmMessage: string;
    trashConfirmButton: string;
    restore: string;
    deletePermanently: string;
    deletePermanentlyConfirmTitle: string;
    deletePermanentlyConfirmMessage: string;
    emptyTrash: string;
    emptyTrashConfirmTitle: string;
    emptyTrashConfirmMessage: string;
    trashNotice: string;
    trashEmpty: string;
    trashDaysLeft: string;
    trashActionFailed: string;
    trashNoPermission: string;
    readOnlyNotice: string;
    readOnlyActionHint: string;
    working: string;
    purgedBadge: string;
    archiveAction: string;
    unarchiveAction: string;
    archivedEmpty: string;
    archiveFailed: string;
    archivedBadge: string;
  };
  projects: {
    createOrder: string;
    createOrderHint: string;
    createProject: string;
    createProjectHint: string;
    newProject: string;
    name: string;
    namePlaceholder: string;
    client: string;
    clientPlaceholder: string;
    reference: string;
    referencePlaceholder: string;
    dueDate: string;
    addNote: string;
    notePlaceholder: string;
    defaults: string;
    task: string;
    translation: string;
    layout: string;
    decidePerDocument: string;
    assignProject: string;
    dropFiles: string;
    chooseFiles: string;
    createCta: string;
    creating: string;
    pastDate: string;
    afterProjectDue: string;
    prepTitle: string;
    addDocuments: string;
    addNDocuments: string;
    languages: string;
    assignTo: string;
    apply: string;
    statusReady: string;
    statusToSetUp: string;
    createNOrders: string;
    estimated: string;
    remainingPrep: string;
    archive: string;
    archiveConfirm: string;
    unarchive: string;
    downloadZip: string;
    setTaskToContinue: string;
    creditsForXN: string;
    createX: string;
    recharge: string;
    position: string;
    auto: string;
    removePrep: string;
  };
  teamPage: {
    title: string;
    subtitle: string;
    inviteMember: string;
    teamMembers: string;
    pendingInvites: string;
    noMembers: string;
    owner: string;
    active: string;
    inactive: string;
    remove: string;
    joined: string;
    pendingInvitations: string;
    expires: string;
    sent: string;
    link: string;
    copied: string;
    resend: string;
    cancel: string;
    teamRoles: string;
    teamRolesDesc: string;
    inviteTeamMember: string;
    invitationSent: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    sending: string;
    sendInvitation: string;
    invitationSentTo: string;
    inviteLink: string;
    shareLinkDesc: string;
    done: string;
    copy: string;
    member: string;
    role: string;
    status: string;
    email: string;
    removeTeamMember: string;
    removeConfirmMsg: string;
    removeMember: string;
    thisMonth: string;
    // Roles y permisos (editor de rol compartido invitar/editar)
    roleOwner: string;
    roleManager: string;
    roleReviewer: string;
    roleMember: string;
    roleManagerDesc: string;
    roleReviewerDesc: string;
    roleMemberDesc: string;
    whatWillTheyDo: string;
    permissionsHint: string;
    whatThisAllows: string;
    scopeDocsViewAll: string;
    scopeDocsApprove: string;
    scopeDocsDelete: string;
    scopeCorpusEdit: string;
    scopeCertManage: string;
    scopeBilling: string;
    scopeTeam: string;
    scopeAccountEdit: string;
    scopeLogs: string;
    scopeDocsCreate: string;
    scopeCertAttach: string;
    adjustedBadge: string;
    permDocsViewAll: string;
    permDocsApprove: string;
    permDocsDelete: string;
    permCorpusEdit: string;
    permCertManage: string;
    permBilling: string;
    permTeam: string;
    permAccountEdit: string;
    permLogs: string;
    permDocsCreate: string;
    permCertAttach: string;
    readOnlyBadge: string;
    adjustedOne: string;
    adjustedMany: string;
    permCountOne: string;
    permCountMany: string;
    edit: string;
    editMemberTitle: string;
    saveChanges: string;
    saving: string;
    readOnlyLabel: string;
    readOnlyHint: string;
    firstNamePlaceholder: string;
    lastNamePlaceholder: string;
    emailPlaceholder: string;
  };
  creditsPage: {
    title: string;
    subtitle: string;
    availableBalance: string;
    creditsInfo: string;
    usedThisMonth: string;
    totalPurchased: string;
    yourSubscription: string;
    cancelSubscription: string;
    currentPlan: string;
    unknown: string;
    subscriptionEnds: string;
    nextBillingDate: string;
    willNotRenew: string;
    paymentMethod: string;
    change: string;
    card: string;
    expiresDate: string;
    autoRenew: string;
    on: string;
    off: string;
    updating: string;
    subscriptionEndingSoon: string;
    subscriptionEndingDesc: string;
    reactivateNow: string;
    cancelSubscriptionQ: string;
    cancelSubscriptionDesc: string;
    keepSubscription: string;
    yesCancel: string;
    changePlanQ: string;
    newPlan: string;
    changePlanDesc: string;
    changePlan: string;
    alreadySubscribed: string;
    alreadySubscribedDesc: string;
    gotIt: string;
    autoRechargeCredits: string;
    whenBalanceBelow: string;
    credits: string;
    rechargeAmount: string;
    autoRecharge: string;
    willAdd: string;
    autoRechargeDisabled: string;
    saveChanges: string;
    saving: string;
    noPaymentMethod: string;
    addPaymentMethodFirst: string;
    addPaymentMethodForRenewal: string;
    addPaymentMethodTitle: string;
    addPaymentMethodForRenewalDesc: string;
    pendingPaymentMethodStatus: string;
    nextBillingAfterPaymentMethod: string;
    buyCreditPackages: string;
    subscriptionRequiredToBuy: string;
    noPackagesAvailable: string;
    mostPopular: string;
    pricingFrom: string;
    contactSalesPrice: string;
    buyNow: string;
    buyCustomAmount: string;
    buyCustomDesc: string;
    enterCreditsMin: string;
    subscriptionPlans: string;
    monthly: string;
    yearly: string;
    saveUpTo: string;
    noPlansAvailable: string;
    year: string;
    month: string;
    orMonthly: string;
    orYearly: string;
    creditsIncluded: string;
    noCreditsIncluded: string;
    imageBasedPages: string;
    creditPerPage: string;
    imageBasedDesc: string;
    textBasedPages: string;
    textBasedDesc: string;
    transactionHistory: string;
    noTransactions: string;
    noTransactionsFilter: string;
    showLess: string;
    txFilterAll: string;
    txFilterToday: string;
    txFilterWeek: string;
    txFilterMonth: string;
    exportCSV: string;
    txCount: string;
    txNet: string;
    purchased: string;
    used: string;
    subscriptionHelp: string;
    subscriptionHelpTitle: string;
    subscriptionHelpContent: string;
    autoRechargeHelp: string;
    autoRechargeHelpTitle: string;
    autoRechargeHelpContent: string;
    viewPlans: string;
    close: string;
    redeemCode: string;
    redeemPlaceholder: string;
    redeemApply: string;
    redeemApplying: string;
    redeemSuccess: string;
    redeemError: string;
    redeemHint: string;
    redeemPreviewTrial: string;
    redeemPreviewDiscount: string;
    redeemPreviewDiscountMonths: string;
    redeemPreviewPurchase: string;
    redeemApplied: string;
    redeemAppliedDiscount: string;
    redeemAppliedMultiplier: string;
    redeemAppliedPacks: string;
    redeemAppliedPlans: string;
    redeemAppliedNone: string;
    planCardCodeDiscount: string;
    planCardCodeMultiplier: string;
  };
  paywall: {
    teamTitle: string;
    teamMessage: string;
    corpusTitle: string;
    corpusMessage: string;
    cta: string;
    openingCheckout: string;
  };
  planGate: {
    teamTitle: string;
    teamBody: string;
    activityTitle: string;
    activityBody: string;
    activateTeamCta: string;
    openingCheckout: string;
    memberTitle: string;
    memberBody: string;
  };
  settingsPage: {
    unsavedTitle: string;
    unsavedMessage: string;
    unsavedSaveAndLeave: string;
    unsavedLeave: string;
    unsavedStay: string;
    emailVerifyBannerTitle: string;
    emailVerifyBannerDesc: string;
    emailVerifySendCode: string;
    emailVerifySending: string;
    emailVerifyCta: string;
    emailVerifyVerifying: string;
    emailVerifyError: string;
    title: string;
    subtitle: string;
    saveChanges: string;
    saving: string;
    saved: string;
    profile: string;
    firstName: string;
    lastName: string;
    email: string;
    accountSection: string;
    companyName: string;
    changeLogo: string;
    uploadLogo: string;
    uploading: string;
    remove: string;
    notificationsSection: string;
    orderStatusUpdates: string;
    orderStatusUpdatesDesc: string;
    teamActivity: string;
    teamActivityDesc: string;
    lowCreditAlerts: string;
    lowCreditAlertsDesc: string;
    marketingEmails: string;
    marketingEmailsDesc: string;
    timezone: string;
    personalInfo: string;
    personalInfoDesc: string;
    whatsappAvailable: string;
    dateFormat: string;
    dateFormatHint: string;
    markerStyleLabel: string;
    seccionEmpresa: string;
    correoEmpresa: string;
    telefonoEmpresa: string;
    seccionEmpresaDesc: string;
    seccionPerfil: string;
    seccionPerfilDesc: string;
    managedByAgency: string;
    usarLaDeLaEmpresa: string;
    perfilNoCargado: string;
    markerStyleHint: string;
    markerBrackets: string;
    markerBraces: string;
    interfaceLanguage: string;
    legalName: string;
    legalNamePlaceholder: string;
    taxId: string;
    taxIdHint: string;
    website: string;
    jobTitle: string;
    jobTitlePlaceholder: string;
    linkedinCompanyPage: string;
    preferredContactMethod: string;
    preferredContactNone: string;
    preferredContactCall: string;
    timezoneDesc: string;
    current: string;
    security: string;
    changePassword: string;
    sessions: string;
    activeSession: string;
    signOutAllDevices: string;
    billing: string;
    currentPlan: string;
    payAsYouGo: string;
    noPlanActive: string;
    noPlanDesc: string;
    noPlanDescFreelancer: string;
    viewInvoices: string;
    changePasswordModal: string;
    changePasswordDesc: string;
    currentPassword: string;
    enterCurrentPassword: string;
    newPassword: string;
    minChars: string;
    confirmNewPassword: string;
    confirmYourPassword: string;
    changingPassword: string;
    passwordChanged: string;
    passwordChangedDesc: string;
    done: string;
    passwordsNoMatch: string;
    passwordMinLength: string;
    logoMaxSize: string;
    logoFormatHint: string;
    helpTitle: string;
    helpDesc: string;
    helpEmail: string;
    helpDocs: string;
    privacyPolicy: string;
    termsConditions: string;
    allRightsReserved: string;
    // Privacy & Data Retention
    privacyRetention: string;
    privacyRetentionDesc: string;
    retentionPolicy: string;
    retentionPolicyDesc: string;
    retentionDisabled: string;
    retentionDays: string;
    retentionSaving: string;
    retentionSaved: string;
    retentionNone: string;
    retentionDay: string;
    retentionDaysLabel: string;
    retentionWarning: string;
    retentionImmediate: string;
    retentionImmediateWarning: string;
    retentionActive: string;
    retentionInactive: string;
    deletionCertificates: string;
    deletionCertificatesDesc: string;
    viewCertificates: string;
    noCertificates: string;
    downloadCertificate: string;
    previewCertificate: string;
    // Timezone picker
    searchCity: string;
    noResults: string;
    // Deletion certificate detail modal
    certDetail: {
      certificateOne: string;
      certificateMany: string;
      backToList: string;
      order: string;
      document: string;
      account: string;
      deletedAt: string;
      method: string;
      retention: string;
      retentionDaysTpl: string;
      integrity: string;
      valid: string;
      invalid: string;
      deletedFilesTpl: string;
      purgedFieldsTpl: string;
      prev: string;
      next: string;
    };
    // Professional Profile
    professionalProfile: string;
    professionalProfileDesc: string;
    phone: string;
    phonePlaceholder: string;
    phoneHint: string;
    address: string;
    addressPlaceholder: string;
    association: string;
    associationNoneOption: string;
    associationOther: string;
    associationCustomLabel: string;
    certificationNumber: string;
    languagePairs: string;
    languagePairsDesc: string;
    addLanguagePair: string;
    sourceLanguage: string;
    targetLanguage: string;
    noLanguagePairs: string;
    removePair: string;
    signatureImage: string;
    signatureHint: string;
    signatureInvalidFormat: string;
    signatureMaxSize: string;
    uploadSignature: string;
    changeSignature: string;
    removeSignature: string;
    saveProfile: string;
    adminAccount: string;
    adminAccountDesc: string;
    role: string;
    subStatusActive: string;
    subStatusTrialing: string;
    subStatusPastDue: string;
    subStatusUnpaid: string;
    subStatusCanceled: string;
    subStatusIncomplete: string;
    subStatusPaused: string;
    searchCountry: string;
    noCountriesFound: string;
    companyWebsitePlaceholder: string;
    companyEmailPlaceholder: string;
    personalWebsitePlaceholder: string;
    linkedinProfilePlaceholder: string;
  };
  certificatePage: {
    title: string;
    subtitle: string;
    uploadDocx: string;
    newTemplate: string;
    editingTemplate: string;
    variables: string;
    saveAndClose: string;
    saving: string;
    insertVariable: string;
    dynamicVariables: string;
    variablesDesc: string;
    clickToCopy: string;
    hideExamples: string;
    showExamples: string;
    defaultTemplates: string;
    myTemplates: string;
    otherTemplates: string;
    setActive: string;
    active: string;
    global: string;
    noTemplate: string;
    noTemplateDesc: string;
    createTemplate: string;
    uploadTemplate: string;
    name: string;
    namePlaceholder: string;
    descriptionOptional: string;
    descriptionPlaceholder: string;
    category: string;
    general: string;
    legal: string;
    immigration: string;
    academic: string;
    cancel: string;
    creating: string;
    createAndEdit: string;
    opening: string;
    editTemplate: string;
    activateHint: string;
    templateLibrary: string;
    willBeAppended: string;
    language: string;
    anyLanguage: string;
    english: string;
    spanish: string;
    portuguese: string;
    german: string;
    french: string;
    clickToRename: string;
    activeTemplate: string;
    copyHint: string;
    copiedHint: string;
    copyVarTitle: string;
    view: string;
    all: string;
    close: string;
    deleteTemplate: string;
  };
  orderDetail: {
    backToOrders: string;
    continueOrder: string;
    error: string;
    orderNotFound: string;
    failedToLoad: string;
    orderDetails: string;
    documentType: string;
    category: string;
    sourceCountry: string;
    targetCountry: string;
    sourceLanguage: string;
    targetLanguage: string;
    totalPages: string;
    formatTemplate: string;
    noFormatTemplate: string;
    credits: string;
    consumed: string;
    estimated: string;
    created: string;
    createdBy: string;
    assignedTo: string;
    completed: string;
    exportFormat: string;
    originalFile: string;
    view: string;
    openInNewTab: string;
    download: string;
    close: string;
    accessDenied: string;
    adminNoAccess: string;
    translatedDocument: string;
    finalDocument: string;
    deletionCertificate: string;
    translationCertificate: string;
    fileDeleted: string;
    sizeUnknown: string;
    uploadAndDetails: string;
    ocr: string;
    review: string;
    final: string;
    deleteData: string;
    deleteOrderData: string;
    deleteOrderDesc: string;
    deleteOrderPreserved: string;
    cancel: string;
    dataDeletedSuccess: string;
    dataDeletedFailed: string;
    certificateLabel: string;
    humanVerified: string;
    verify: string;
    verifyConfirmTitle: string;
    verifyConfirmDesc: string;
    verifyNotes: string;
    verifySuccess: string;
    verifyFailed: string;
    verifiedBy: string;
    verifiedOn: string;
    viewVerification: string;
    // Data purged / deletion box
    dataPurged: string;
    loadingCertificate: string;
    allDataDeleted: string;
    filesDeletedTpl: string;
    autoPurgeNoticeTpl: string;
    retentionMin: string;
    retentionHour: string;
    retentionHours: string;
    retentionDay: string;
    retentionDays: string;
    inTrashBanner: string;
    restoreOrder: string;
    readOnlyNotice: string;
    finalizationPendingApprover: string;
    reviewAndFinalize: string;
    returnToRequester: string;
    returnNotePlaceholder: string;
    returnConfirm: string;
    returnFailed: string;
    finalizationPendingRequester: string;
    finalizationReturned: string;
    finalizationReturnedNote: string;
    someoneFromTeam: string;
    // Localized document type & category labels
    documentTypes: { image: string; text: string; handwritten: string };
    categories: {
      birth_certificate: string;
      certificate: string;
      diploma: string;
      passport: string;
      marriage_license: string;
      medical: string;
      legal: string;
      immigration: string;
      other: string;
      academic_transcript: string;
      civil_registry: string;
      death_certificate: string;
      power_of_attorney: string;
      general: string;
    };
  };
  workspace: {
    title: string;
    welcomeBack: string;
    welcomeBackOne: string;
    welcomeBackOther: string;
    assigned: string;
    tasksToComplete: string;
    active: string;
    inProgress: string;
    completed: string;
    thisMonth: string;
    pendingTasks: string;
    pendingTasksDesc: string;
    noOrdersAssigned: string;
    ordersWillAppear: string;
    page: string;
    pages: string;
    workflowTip: string;
    workflowTipDesc: string;
    startOrder: string;
    continueAction: string;
    reviewAction: string;
    viewAction: string;
    myOrders: string;
    myOrdersSubtitle: string;
    searchPlaceholder: string;
    totalAssigned: string;
    noOrdersFound: string;
    ordersAssignedAppear: string;
    document: string;
    pagesCol: string;
    languages: string;
    status: string;
    createdCol: string;
    action: string;
    pageOf: string;
    orders: string;
    creditPackages: string;
    purchaseCreditsDesc: string;
    viewAll: string;
    uploadPhoto: string;
    managePreferences: string;
    notifications: string;
    newTaskAssignments: string;
    newTaskAssignmentsDesc: string;
    taskDeadlines: string;
    taskDeadlinesDesc: string;
    commentsAndFeedback: string;
    commentsAndFeedbackDesc: string;
    dailySummary: string;
    dailySummaryDesc: string;
    account: string;
    organization: string;
    role: string;
    yourStats: string;
    tasksCompleted: string;
    pagesTranslated: string;
    avgQualityScore: string;
    loadError: string;
  };
  newOrder: {
    loadingOrder: string;
    restoreProgress: string;
    stepProgress: string;
    stepUploadDetails: string;
    stepProcessing: string;
    stepReview: string;
    stepTranslation: string;
    stepDownload: string;
    modeLabel: string;
    modeTranslate: string;
    modeLayout: string;
    modeTranslateHint: string;
    modeLayoutHint: string;
    layoutReadyTitle: string;
    layoutReadyDesc: string;
    downloadDocument: string;
    preparingDownload: string;
    error: string;
    uploadDocument: string;
    acceptedFileTypes: string;
    analyzingDocument: string;
    classifyingDocument: string;
    loadingPreview: string;
    fileTypePdf: string;
    fileTypeWord: string;
    docxTooLargeToCount: string;
    fileTypeImage: string;
    page: string;
    pages: string;
    hasText: string;
    scanned: string;
    fileAnalysis: string;
    complete: string;
    textLabel: string;
    selectable: string;
    methodLabel: string;
    ocrMethod: string;
    directMethod: string; paperLetter: string; paperA4: string; paperLegal: string; paperPortrait: string; paperLandscape: string;
    handwritten: string;
    mixedContent: string;
    disableHandwritten: string;
    enableHandwritten: string;
    manuscriptSuggestionTitle: string;
    manuscriptSuggestionBody: string;
    manuscriptSuggestionConfirm: string;
    manuscriptSuggestionDismiss: string;
    manuscriptSuggestionBadge: string;
    pageFormat: string;
    pageFormatLetter: string;
    pageFormatLegal: string;
    orientationLabel: string;
    orientationPortrait: string;
    orientationLandscape: string;
    orientationSuggested: string;
    readAloud: string;
    stopReading: string;
    loadingAudio: string;
    viewTranscript: string;
    transcriptTitle: string;
    copyText: string;
    transcribingHandwriting: string;
    readingHandwriting: string;
    processingPages: string;
    generatingDocument: string;
    clientName: string;
    clientNamePlaceholder: string;
    reference: string;
    dueDate: string;
    addInstructions: string;
    detailsSection: string;
    languagesSection: string;
    teamSection: string;
    documentName: string;
    documentNamePlaceholder: string;
    documentCategory: string;
    sourceCountry: string;
    targetCountry: string;
    sourceLanguage: string;
    targetLanguage: string;
    chooseTargetLanguage: string;
    targetLanguageRequired: string;
    searchLanguage: string;
    searchCountry: string;
    noSearchResults: string;
    sameLanguageBlocked: string;
    autoDeleteLabel: string;
    autoDeleteAccountDefault: string;
    autoDeleteImmediate: string;
    autoDelete20min: string;
    autoDelete1h: string;
    autoDelete6h: string;
    autoDelete1d: string;
    autoDelete7d: string;
    autoDelete30d: string;
    autoDelete90d: string;
    estimatedCredits: string;
    creditCalculation: string;
    assignToTeamMember: string;
    notAssigned: string;
    notesInstructions: string;
    teamNotesPlaceholder: string;
    assigning: string;
    analyzing: string;
    assignNotify: string;
    questionOnPage: string;
    questionNoPosition: string;
    fileTooLargeDetail: string;
    unsupportedFileDetail: string;
    uploadingOriginal: string;
    uploadFailedMessage: string;
    retryUpload: string;
    /** La subida del original no salió tras los reintentos. */
    originalUploadFailed: string;
    /** ARCHIVO_VACIO: el backend recibió el original vacío o sin él. */
    originalIncomplete: string;
    /** Orden retomada sin su archivo: hay que volver a elegirlo. */
    originalMissingTitle: string;
    /** {name} y {size}: el archivo que tenía la orden. */
    originalMissingMessage: string;
    originalMissingShort: string;
    originalDamaged: string;
    /** El archivo elegido no es el de la orden: se empezará una orden nueva. */
    originalDifferentFile: string;
    /** No se guardó la copia del original pero se sigue procesando; se reintenta en segundo plano. */
    originalCopyPendingTitle: string;
    originalCopyPending: string;
    startOrder: string;
    analyzeContinue: string;
    documentPreview: string;
    wordDocument: string;
    contentPreview: string;
    noPreviewAvailable: string;
    processing: string;
    resumingProcessing: string;
    convertingDocument: string;
    generatingPreview: string;
    editDocument: string;
    viewOriginal: string;
    saving: string;
    backToReview: string;
    originalDocument: string;
    back: string;
    exitOrderTitle: string;
    exitOrderMessage: string;
    exitOrderConfirm: string;
    exitOrderCancel: string;
    documentReview: string;
    of: string;
    stopAutoscroll: string;
    autoscroll: string;
    translating: string;
    loadingOriginal: string;
    loadingShort: string;
    downloadDocx: string;
    closeOrderTitle: string;
    closeOrderDesc: string;
    closeOrderYes: string;
    closeOrderNo: string;
    closeOrderFailed: string;
    closeOrderSaving: string;
    reflowPagesBtn: string;
    reflowPagesError: string;
    dropHere: string; clickBrowse: string; selectFile: string; supportedLabel: string; progressMessages: Record<string, string>; rotatePage: string; prevPage: string; nextPage: string; zoomIn: string;
    zoomOut: string;
    fitToWidth: string;
    pdfPreviewError: string;
    continueToTranslation: string;
    panelOriginal: string;
    panelPreview: string;
    edit: string;
    previewNotAvailable: string;
    ocrResultsComparison: string;
    textBlocksDetected: string;
    accuracy: string;
    backToComparison: string;
    editTranslation: string;
    translation: string;
    panelTranslation: string;
    updatingPreview: string;
    translationPreviewNotAvailable: string;
    finishOrder: string;
    orderCompleted: string;
    orderDownloaded: string;
    orderDownloadedLayout: string;
    redirecting: string;
    finishOrderDesc: string;
    finishOrderDescLayout: string;
    document: string;
    exportFormat: string;
    formatPdf: string;
    formatOdt: string;
    translationLabel: string;
    autoLanguage: string;
    creditsToCharge: string;
    creditsCharged: string;
    currentBalanceLabel: string;
    credits: string;
    creditBalance: string;
    cancel: string;
    downloadComplete: string;
    insufficientCredits: string;
    insufficientCreditsDesc: string;
    /** Igual que insufficientCreditsDesc cuando {required} pide singular (1 crédito). */
    insufficientCreditsDescOne: string;
    creditsNeeded: string;
    yourBalance: string;
    shortfall: string;
    buyCredits: string;
    upgradePlan: string;
    bestValue: string;
    contactOwner: string;
    preparingTexts: string;
    translatingBlocks: string;
    translatingBlocksOne: string;
    applyingTranslations: string;
    includeCertificate: string;
    savingDocument: string;
    autosavePending: string;
    autosaveSaving: string;
    autosaveSaved: string;
    autosaveError: string;
    editorCountWord: string;
    editorCountWords: string;
    editorCountChar: string;
    editorCountChars: string;
    editorCountNoSpaces: string;
    translatingProgress: string;
    generatingTranslatedPreview: string;
    finalizingTranslation: string;
    translationFailed: string;
    wordSinTexto: string;
    translationPartialTitle: string;
    translationPartialMessage: string;
    yes: string;
    no: string;
    protectPdf: string;
    protectPdfHint: string;
    outputOptions: string;
    certificateTemplate: string;
    selectTemplate: string;
    noTemplates: string;
    addTemplate: string;
    humanVerifiedSeal: string;
    attachOriginal: string;
    attachOriginalHint: string;
    attachOriginalPosition: string;
    attachOriginalAtStart: string;
    attachOriginalAtEnd: string;
    downloadPdf: string;
    downloadDoc: string;
    reviewDocument: string;
    chooseFormat: string;
    optionWord: string;
    optionHtml: string;
    optionMix: string;
    optionFormat: string;
    optionFormatHint: string;
    optionFormatImages: string;
    docHeaderLabel: string;
    docFooterLabel: string;
    panelTranscription: string;
    hideEditorToolbar: string;
    showEditorToolbar: string;
    verifyDocumentTitle: string;
    verifyDocumentDesc: string;
    verificationLabel: string;
    correctionPlaceholder: string;
    detectedLabel: string;
    editedIndicator: string;
    hidePin: string;
    showPin: string;
    skip: string;
    confirm: string;
    localizationNoticeTitle: string;
    localizationNoticeIntro: string;
    localizationNoticeSummaryTpl: string;
    localizationNoticeSummaryAnd: string;
    localizationNoticeDatesOne: string;
    localizationNoticeDatesMany: string;
    localizationNoticeFiguresOne: string;
    localizationNoticeFiguresMany: string;
    localizationNoticeExamplesTitle: string;
    localizationNoticeChecking: string;
    localizationNoticeMoreTpl: string;
    localizationNoticeTimesMany: string;
    localizationNoticeTypeDate: string;
    localizationNoticeTypeFigure: string;
    localizationNoticeSafeTitle: string;
    localizationNoticeSafeBody: string;
    localizationNoticeAdapt: string;
    localizationNoticeKeep: string;
    localizationNoticeRemember: string;
    localizationNoticeRememberHint: string;
    localizationNoticeClose: string;
    localizationNoticeIdsLabel: string;
    localizationNoticeIdsOne: string;
    localizationNoticeIdsMany: string;
    localizationNoticeIdsUnchanged: string;
    localizationNoticeShowExamples: string;
    localizationNoticeHideExamples: string;
    noCreatePermissionTitle: string;
    noCreatePermissionDesc: string;
    goToMyOrders: string;
    finishNoPermissionTitle: string;
    finishNoPermissionDesc: string;
    finishReadOnlyDesc: string;
    sendForReview: string;
    sendingForReview: string;
    reviewRequestSent: string;
    reviewRequestSentNoNotice: string;
    reviewAlreadyPending: string;
    reviewRequestFailed: string;
    certificateNoPermission: string;
  };
  certVars: {
    translatorName: string; translatorNameDesc: string;
    translatorEmail: string; translatorEmailDesc: string;
    requesterName: string; requesterNameDesc: string;
    certificationDate: string; certificationDateDesc: string;
    orderDate: string; orderDateDesc: string;
    completionDate: string; completionDateDesc: string;
    sourceLanguage: string; sourceLanguageDesc: string;
    targetLanguage: string; targetLanguageDesc: string;
    sourceCountry: string; sourceCountryDesc: string;
    targetCountry: string; targetCountryDesc: string;
    documentTitle: string; documentTitleDesc: string;
    documentType: string; documentTypeDesc: string;
    originalFile: string; originalFileDesc: string;
    pageCount: string; pageCountDesc: string;
    orderNumber: string; orderNumberDesc: string;
    projectNumber: string; projectNumberDesc: string;
    companyName: string; companyNameDesc: string;
    orderNotes: string; orderNotesDesc: string;
    // Translator profile variables
    translatorPhone: string; translatorPhoneDesc: string;
    associationName: string; associationNameDesc: string;
    certificationNumber: string; certificationNumberDesc: string;
    languagePairs: string; languagePairsDesc: string;
    translatorAddress: string; translatorAddressDesc: string;
    translatorCity: string; translatorCityDesc: string;
    translatorState: string; translatorStateDesc: string;
    translatorCountry: string; translatorCountryDesc: string;
    translatorZip: string; translatorZipDesc: string;
  };
  auth: {
    invalidCredentials: string;
    userExists: string;
    accountDeactivated: string;
    accountLocked: string;
    accountLockedOne: string;
    accountLockedOther: string;
    tooManyAttemptsLocked: string;
    tooManyAttemptsLockedOne: string;
    tooManyAttemptsLockedOther: string;
    verifyEmailTitle: string;
    verifyEmailDesc: string;
    verifyEmailCta: string;
    verifyEmailWrong: string;
    welcomeBack: string;
    signInSubtitle: string;
    createAccount: string;
    startFreeTrial: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    registerAsCompany: string;
    companyName: string;
    companyNamePlaceholder: string;
    companyLogo: string;
    emailAddress: string;
    emailPlaceholder: string;
    password: string;
    passwordPlaceholder: string;
    enterPassword: string;
    signIn: string;
    signingIn: string;
    creatingAccount: string;
    uploadingLogo: string;
    forgotPassword: string;
    noAccount: string;
    createAnAccount: string;
    alreadyHaveAccount: string;
    signInLink: string;
    continueWithGoogle: string;
    continueWithLinkedIn: string;
    orEmailPassword: string;
    oauthFailed: string;
    oauthGoogleOriginError: string;
    authFailed: string;
    connectionError: string;
    tooManyRequests: string;
    sessionError: string;
    // Password requirements
    pwdMinLength: string;
    pwdUppercase: string;
    pwdLowercase: string;
    pwdNumber: string;
    pwdSpecialChar: string;
    pwdStrengthWeak: string;
    pwdStrengthFair: string;
    pwdStrengthGood: string;
    pwdStrengthStrong: string;
    // Forgot password
    forgotPasswordTitle: string;
    forgotPasswordDesc: string;
    sendCode: string;
    sendingCode: string;
    verificationCode: string;
    verificationCodeDesc: string;
    verifyCode: string;
    verifyingCode: string;
    resendCode: string;
    resendIn: string;
    newPassword: string;
    confirmPassword: string;
    confirmPasswordPlaceholder: string;
    resetPassword: string;
    resettingPassword: string;
    passwordResetSuccess: string;
    passwordResetSuccessDesc: string;
    backToSignIn: string;
    passwordsNoMatch: string;
    passwordMinLength: string;
    // Inactivity
    sessionExpired: string;
    sessionExpiredDesc: string;
    // Success
    accountCreatedSuccess: string;
    // Auth page chrome + mockup
    backToHome: string;
    mockupTitleOrders: string;
    mockupDescOrders: string;
    mockupTitleEditor: string;
    mockupDescEditor: string;
    mockupTitleTeam: string;
    mockupDescTeam: string;
    mockupTitleCredits: string;
    mockupDescCredits: string;
    mockupTabOrders: string;
    mockupTabEditor: string;
    mockupTabTeam: string;
    mockupTabCredits: string;
    mockupNavOrders: string;
    mockupNavWorkspace: string;
    mockupNavTeam: string;
    mockupNavCredits: string;
    mockupNavSettings: string;
    mockupHeaderOrders: string;
    mockupHeaderWorkspace: string;
    mockupHeaderCompanyDashboard: string;
    mockupHeaderCreditsBilling: string;
    mockupSearchOrders: string;
    mockupNewOrder: string;
    mockupColDocument: string;
    mockupColLanguage: string;
    mockupColStatus: string;
    mockupColDate: string;
    mockupStatusCompleted: string;
    mockupStatusInReview: string;
    mockupStatusProcessing: string;
    mockupStatusPending: string;
    mockupStepUpload: string;
    mockupStepOcr: string;
    mockupStepReview: string;
    mockupStepExport: string;
    mockupOriginalLabel: string;
    mockupTranslationLabel: string;
    mockupTotalOrders: string;
    mockupTeamMembers: string;
    mockupThisMonth: string;
    mockupTeamActivity: string;
    mockupRoleTranslator: string;
    mockupRoleReviewer: string;
    mockupRoleAdmin: string;
    mockupRowOrders: string;
    mockupAvailableCredits: string;
    mockupProPlan: string;
    mockupBusinessPlan: string;
    mockupRecentTransactions: string;
    mockupCredits: string;
    logoTooLarge: string;
    mockupCompanyAdmin: string;
    mockupDocBirthCertificate: string;
    mockupDocMarriageLicense: string;
    mockupDocDiploma: string;
    mockupDocMedicalRecord: string;
    mockupDocPowerOfAttorney: string;
    mockupTxBirthCertificate: string;
    mockupTxCreditPurchase: string;
    mockupTxDiplomaOcr: string;
  };
  activityPage: {
    title: string;
    subtitle: string;
    exportCSV: string;
    totalActivities: string;
    clients: string;
    ocrOperations: string;
    translations: string;
    searchPlaceholder: string;
    allActions: string;
    ocrGenerated: string;
    ocrEdited: string;
    translationGenerated: string;
    translationEdited: string;
    downloadAction: string;
    statusCompleted: string;
    member: string;
    time: string;
    order: string;
    action: string;
    details: string;
    loading: string;
    noActivity: string;
    showing: string;
    of: string;
    total: string;
    orderCreated: string;
    ocrCompleted: string;
    translationGen: string;
    translationEdit: string;
    processing: string;
    completed: string;
    translationReview: string;
    ocrReview: string;
    orderAssigned: string;
    tasksAssigned: string;
    allMembers: string;
    client: string;
    noClient: string;
    clearDates: string;
    role: string;
    orderTrashed: string;
    orderRestored: string;
    orderArchived: string;
    orderUnarchived: string;
    orderDeleted: string;
    orderDeletedAuto: string;
    trashEmptied: string;
    orderReassigned: string;
    orderUnassigned: string;
    referenceChanged: string;
    orderCancelled: string;
    humanVerification: string;
    projectCreated: string;
    otherAction: string;
    loadError: string;
    finalizationRequested: string;
    finalizationReturned: string;
    creditClaimSubmitted: string;
    creditClaimFileOpened: string;
    creditClaimRefunded: string;
    languagesConfirmed: string;
    languagesDecided: string;
    formatBandsDecided: string;
  };
  corpus: {
    title: string;
    subtitle: string;
    folders: string;
    createFolder: string;
    createCorpus: string;
    createError: string;
    glossary: string;
    documents: string;
    style: string;
    knowledge: string;
    sourceTerm: string;
    targetTerm: string;
    context: string;
    addTerm: string;
    importCsv: string;
    exportCsv: string;
    importPreview: string;
    importConfirm: string;
    importSuccess: string;
    importDuplicates: string;
    importErrors: string;
    importTotal: string;
    importDragOrClick: string;
    importCsvFormat: string;
    importing: string;
    export: string;
    terms: string;
    noCorpus: string;
    selectCorpus: string;
    formal: string;
    neutral: string;
    plain: string;
    noFolders: string;
    noEntries: string;
    search: string;
    name: string;
    description: string;
    sourceLanguage: string;
    targetLanguage: string;
    customInstructions: string;
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    active: string;
    inactive: string;
    corpora: string;
    folder: string;
    back: string;
    create: string;
    showing: string;
    of: string;
    noResults: string;
    confirmDeleteFolder: string;
    confirmDeleteCorpus: string;
    confirmDeleteTerm: string;
    prev: string;
    next: string;
    comingSoonMilestone: string;
    folderPlaceholder: string;
    corpusNamePlaceholder: string;
    instructionsPlaceholder: string;
    noFolder: string;
    color: string;
    // Redesign keys
    whatIsCorpus: string;
    whatIsCorpusDesc: string;
    corpusInFolder: string;
    shared: string;
    category: string;
    writingStyle: string;
    formalDesc: string;
    neutralDesc: string;
    plainDesc: string;
    createNewCorpus: string;
    configureDesc: string;
    corpusNameRequired: string;
    corpusNameExample: string;
    corpusUseDesc: string;
    exampleDocs: string;
    exampleDocsDesc: string;
    originalDoc: string;
    translatedDoc: string;
    docPairTip: string;
    knowledgeDesc: string;
    uploadArticle: string;
    acceptedFormats: string;
    dragFileHere: string;
    tmxCsvFormat: string;
    addManually: string;
    generateAI: string;
    share: string;
    viewTabs: string;
    viewScroll: string;
    unorganized: string;
    add: string;
    styleExtracted: string;
    noDocuments: string;
    noKnowledge: string;
    articles: string;
    uploadPair: string;
    notes: string;
    found: string;
    sourceColumn: string;
    targetColumn: string;
    removeFile: string;
    glossaryFileError: string;
    glossarySpreadsheetError: string;
    glossaryTooManyTerms: string;
    glossaryEmptyError: string;
    docFileError: string;
    knowledgeFileError: string;
    fileTooLarge: string;
    uploadingGlossary: string;
    uploadingDocuments: string;
    uploadingKnowledge: string;
    quickStartTitle: string;
    step1Title: string;
    step1Desc: string;
    step2Title: string;
    step2Desc: string;
    step3Title: string;
    step3Desc: string;
    termsTotal: string;
    pageRange: string;
    importConfirmTerms: string;
    close: string;
    badgeAi: string;
    badgeLearned: string;
    confirmDeleteDocument: string;
    confirmDeleteKnowledge: string;
    loadError: string;
    planLimitError: string;
    uploadingFile: string;
    deleteFolder: string;
  };
  admin: {
    asistente: {
      title: string;
      subtitle: string;
      inputPlaceholder: string;
      send: string;
      thinking: string;
      suggestedTitle: string;
      suggestion1: string;
      suggestion2: string;
      suggestion3: string;
      suggestion4: string;
      suggestion5: string;
      exportButton: string;
      emptyState: string;
      errorGeneric: string;
      sendReportNow: string;
      sendReportSending: string;
      sendReportSuccess: string;
      sendReportError: string;
      dashboardSegments: string;
      dashboardAlerts: string;
      dashboardLoadError: string;
      chatSectionTitle: string;
      segmentTrial: string;
      segmentNoUsage: string;
      segmentPartial: string;
      segmentFull: string;
      newAccountsToday: string;
      ambassadors: string;
      suspendedAccounts: string;
      inactiveStatusAccounts: string;
      alertInactive: string;
      alertUsageDrop: string;
      alertLowConsumption: string;
      alertUncharged: string;
      alertDuplicates: string;
      alertScheduledCancellation: string;
      downloadReport: string;
      downloadingReport: string;
      downloadReportError: string;
      promptSegmentListTpl: string;
      promptNewAccounts: string;
      promptAmbassadors: string;
      promptScheduledCancellation: string;
      promptInactive: string;
      promptUsageDrop: string;
      promptLowConsumption: string;
      promptUncharged: string;
      promptDuplicates: string;
      activityTrendTitle: string;
      newAccountsSeriesLabel: string;
      ordersSeriesLabel: string;
      segmentsDistributionTitle: string;
      alertsOverviewTitle: string;
      assistantCardTitle: string;
      quickAction1: string;
      quickAction2: string;
      quickAction3: string;
      quickAction4: string;
      vsYesterday: string;
      openLauncher: string;
      usageRateTitle: string;
      usageRateCaption: string;
      atRiskTitle: string;
      colAccount: string;
      colPlan: string;
      colInactive: string;
      colCredits: string;
      colStatus: string;
      viewHtmlReport: string;
      alertShortCancellation: string;
      alertShortInactive: string;
      alertShortUsageDrop: string;
      alertShortLowConsumption: string;
      alertShortUncharged: string;
      alertShortDuplicates: string;
      assistantHeroTitle: string;
      assistantHeroSubtitle: string;
      assistantSendButton: string;
      agentSummaryTitle: string;
      agentSummarySubtitle: string;
      agentSummaryEmpty: string;
      todayActionsTitle: string;
      todayActionsEmpty: string;
      accountStateTitle: string;
      accountStateSubtitle: string;
      segmentDescTrial: string;
      segmentDescNoUsage: string;
      segmentDescPartial: string;
      segmentDescFull: string;
      alertsOpenTitle: string;
      viewAllAlerts: string;
      riskTableTitle: string;
      riskTableSubtitle: string;
      riskTableCaption: string;
      viewAll: string;
      colEstado: string;
      colSignal: string;
      colLastActivity: string;
      lastActivityToday: string;
      lastActivityYesterday: string;
      lastActivityDaysAgo: string;
      reportsHistoryTitle: string;
      configureSending: string;
      openReport: string;
      chartReadingLabel: string;
      reportOpenError: string;
      biBadge: string;
      statusSentToday: string;
      statusNotSentYetAuto: string;
      statusAutoOff: string;
      nextSendLabel: string;
      nextSendToday: string;
      nextSendTomorrow: string;
    };
    translationEngines: {
      header: string;
      subtitle: string;
      tab: string;
      deeplLabel: string;
      deeplDesc: string;
      claudeLabel: string;
      claudeDesc: string;
      corpusLabel: string;
      corpusDesc: string;
      alwaysOn: string;
      bothDisabled: string;
      effectiveTitle: string;
      effectiveWithCorpus: string;
      effectiveWithoutCorpusPrefix: string;
      noneEnabled: string;
    };
    orderDetail: {
      back: string;
      documentInfo: string;
      category: string;
      type: string;
      pages: string;
      step: string;
      exportFormat: string;
      certificate: string;
      digitalSignature: string;
      humanVerified: string;
      attachOriginal: string;
      originalFile: string;
      translation: string;
      sourceLanguage: string;
      targetLanguage: string;
      sourceCountry: string;
      targetCountry: string;
      translationMethod: string;
      people: string;
      account: string;
      createdBy: string;
      creditsAndDates: string;
      creditsEstimated: string;
      creditsConsumed: string;
      created: string;
      updated: string;
      completed: string;
      processingTime: string;
      platformCostBreakdown: string;
      service: string;
      usage: string;
      costUsd: string;
      total: string;
      yes: string;
      no: string;
      mode: string;
      referenceNumber: string;
      clientName: string;
      dueDate: string;
      verificationId: string;
      viewVerification: string;
      humanVerifiedBy: string;
      translationApprovedBy: string;
      filesPurgedTpl: string;
      deletionCertificateLink: string;
      notFound: string;
      fetchFailed: string;
      formatTemplate: string;
      assignedTo: string;
      notes: string;
      usagePagesTpl: string;
      usageTokensTpl: string;
      usageCharsTpl: string;
      claudeOcr: string;
      claudeTranslation: string;
      claudeVision: string;
      modeDeeplClaude: string;
      modeGoogle: string;
      modeCorpus: string;
    };
    common: {
      systemOnline: string;
      allTime: string;
      realTime: string;
      search: string;
      save: string;
      cancel: string;
      create: string;
      edit: string;
      delete: string;
      close: string;
      done: string;
      loading: string;
      saving: string;
      success: string;
      error: string;
      confirmDelete: string;
      cannotBeUndone: string;
      actions: string;
      status: string;
      name: string;
      description: string;
      title: string;
      category: string;
      type: string;
      date: string;
      language: string;
      active: string;
      inactive: string;
      draft: string;
      total: string;
      page: string;
      of: string;
      previous: string;
      next: string;
      showing: string;
      noResults: string;
      allStatus: string;
      exportCSV: string;
      view: string;
      add: string;
      remove: string;
      refresh: string;
      copy: string;
      copied: string;
      select: string;
      applying: string;
    };
    overview: {
      title: string;
      subtitle: string;
      totalOrders: string;
      activeAccounts: string;
      totalUsers: string;
      creditsBalance: string;
      humanVerified: string;
      totalRevenue: string;
      today: string;
      thisWeek: string;
      thisMonth: string;
      platformCostsAllTime: string;
      totalCost: string;
      avgPerOrder: string;
      avgPerPage: string;
      ordersProcessed: string;
      totalPages: string;
      costByService: string;
      awsTextract: string;
      googleDocAI: string;
      claudeAI: string;
      deepl: string;
      ocrExtraction: string;
      ocrComplementary: string;
      visionPostEdit: string;
      translation: string;
      pages: string;
      costPerPage: string;
      ocrColor: string;
      visionClass: string;
      postEdit: string;
      characters: string;
      words: string;
      costPerMillion: string;
      detailedUsage: string;
      service: string;
      purpose: string;
      usage: string;
      cost: string;
      ocrTextExtraction: string;
      ocrColorDetection: string;
      visionClassification: string;
      translationPostEdit: string;
      totalPlatformCost: string;
      tokens: string;
      chars: string;
      topLanguagePairs: string;
      ordersByStatus: string;
      noLanguagePairData: string;
      percentOfOrders: string;
      orders: string;
      noOrderData: string;
      totalOrdersLabel: string;
      activitySummary: string;
      ordersToday: string;
      creditsConsumed: string;
      completed: string;
      inProgress: string;
      totalAccounts: string;
      newThisWeek: string;
      newThisMonth: string;
      verified: string;
      ofCompleted: string;
      sold: string;
      consumed: string;
      creditsSold: string;
      totalPurchased: string;
      unusedAcrossAccounts: string;
      used: string;
      remaining: string;
      available: string;
      balance: string;
      imageBased: string;
      textBased: string;
      documents: string;
      documentsByType: string;
      pagesByType: string;
      imagePages: string;
      textPages: string;
      noDocData: string;
      noPageData: string;
      activeUsers: string;
      teamUsers: string;
      memberUsers: string;
      statusUploaded: string;
      statusProcessing: string;
      statusOcrReview: string;
      statusTranslationReview: string;
      statusCompleted: string;
      plusTodayTpl: string;
      plusThisWeekTpl: string;
      costEnvTitle: string;
      apply: string;
      salesHint: string;
      salesTitle: string;
      salesModeAll: string;
      salesModeLive: string;
      salesModeTest: string;
      opsOption1: string;
      opsOption2: string;
      opsCorpus: string;
      envProduction: string;
      claudeEnvErrorTpl: string;
      totalTokens: string;
      vision: string;
      docClassification: string;
      classifications: string;
      tokensLabel: string;
      costLabel: string;
      infraTitle: string;
      infraNote: string;
      infraNotConnectedTpl: string;
      usersOverview: string;
      activeCountTpl: string;
      usersUnit: string;
      userTypeTeam: string;
      userTypeMember: string;
      userTypeFreelance: string;
      userTypeAdmin: string;
      docTypeTitle: string;
      docTypeNote: string;
      docsUnit: string;
      pagesUnit: string;
      documentsLabel: string;
      pagesProcessed: string;
      docTypeCountsTpl: string;
    };
    accounts: {
      title: string;
      subtitle: string;
      searchPlaceholder: string;
      noAccountsFound: string;
      account: string;
      owner: string;
      members: string;
      plan: string;
      credits: string;
      created: string;
      payAsYouGo: string;
      activate: string;
      suspend: string;
      viewDetails: string;
      addCredits: string;
      statusActive: string;
      statusTrial: string;
      statusSuspended: string;
      statusInactive: string;
      adjustCredits: string;
      currentBalance: string;
      creditsAmount: string;
      amountPlaceholder: string;
      amountHint: string;
      reason: string;
      reasonPlaceholder: string;
      newBalance: string;
      creditsAppliedSuccess: string;
      failedToAdjust: string;
      networkError: string;
      applyCredits: string;
      applying: string;
      showing: string;
      accountsLabel: string;
      changePlan: string;
      noPlan: string;
      assignPlan: string;
      selectPlan: string;
      billingCycle: string;
      monthly: string;
      yearly: string;
      applyCreditsLabel: string;
      currentPlan: string;
      newPlan: string;
      planChangedSuccess: string;
      failedToChangePlan: string;
      freelanceOnly: string;
      companyPlan: string;
      removePlan: string;
      memberLimitExceeded: string;
      freeMembersKeepHint: string;
      freeCreditModeLabel: string;
      freeCreditKeepPlus5: string;
      freeCreditOnly5: string;
      removePlanWarning: string;
      ownerViewHint: string;
      lastActive: string;
      never: string;
      trialColumn: string;
      trialExpired: string;
      trialOneDay: string;
      trialDays: (n: number) => string;
      planPriceLineTpl: string;
    };
    users: {
      title: string;
      subtitle: string;
      searchPlaceholder: string;
      user: string;
      email: string;
      accountLabel: string;
      userType: string;
      role: string;
      lastLogin: string;
      status: string;
      noUsersFound: string;
      allTypes: string;
      allRoles: string;
      typeAdmin: string;
      typeTeam: string;
      typeMember: string;
      typeUser: string;
      roleOwner: string;
      roleAdmin: string;
      roleManager: string;
      roleMember: string;
      roleFreelancer: string;
      activate: string;
      deactivate: string;
      impersonate: string;
      viewActivity: string;
      never: string;
      showing: string;
      usersLabel: string;
      subtitleActivity: string;
      exportExcel: string;
      exportEmail: string;
      exportPlan: string;
      exportCreditsAvailable: string;
      exportConsumedAccount: string;
      exportConsumedUser: string;
      onlineNow: string;
      activeToday: string;
      byRole: string;
      teamShort: string;
      memberShort: string;
      userShort: string;
      allActivity: string;
      filterOnline: string;
      filterRecent: string;
      filterActiveStatus: string;
      filterSuspendedStatus: string;
      statusOnline: string;
      statusRecently: string;
      statusOffline: string;
      statusSuspendedBadge: string;
      lastActive: string;
      lastPage: string;
      joined: string;
      noAccount: string;
      moreAccountsTpl: string;
      showingRangeTpl: string;
      timeJustNow: string;
      timeMinAgoTpl: string;
      timeHourAgoTpl: string;
      timeDayAgoTpl: string;
      timeWeekAgoTpl: string;
      platformAdminLabel: string;
      platformAdminBadge: string;
      platformAdminDescription: string;
      platformAdminReloginHint: string;
      platformAdminGrant: string;
      platformAdminRevoke: string;
      platformAdminGranted: string;
      platformAdminRevoked: string;
      platformAdminFailed: string;
      platformAdminConfirmGrant: string;
      platformAdminConfirmRevoke: string;
      deleteUser: string;
      deleteTitle: string;
      deleteWarningTpl: string;
      deleteWarningStrong: string;
      sendCode: string;
      enterCodeTpl: string;
      adminEmailFallback: string;
      deletePermanently: string;
      codeSendFailed: string;
      invalidCode: string;
      somethingWrong: string;
      userDeleted: string;
      userDeletedTpl: string;
      exportSheet: string;
    };
    orders: {
      title: string;
      subtitle: string;
      searchPlaceholder: string;
      totalOrders: string;
      completed: string;
      inProgress: string;
      totalPlatformCost: string;
      orderNum: string;
      document: string;
      account: string;
      pages: string;
      credits: string;
      quality: string;
      cost: string;
      status: string;
      date: string;
      actions: string;
      viewBtn: string;
      logsBtn: string;
      noOrdersFound: string;
      platformCostBreakdown: string;
      awsTextract: string;
      googleDocAI: string;
      claudeAI: string;
      deepl: string;
      grandTotal: string;
      allServicesCombined: string;
      detailedUsageBreakdown: string;
      allTimeAggregated: string;
      service: string;
      purpose: string;
      usage: string;
      costUsd: string;
      ocrTextExtraction: string;
      ocrComplementary: string;
      visionClassification: string;
      translationClaude: string;
      translation: string;
      totalLbl: string;
      orderLogsTitle: string;
      loadingLogs: string;
      noLogsFound: string;
      timestamp: string;
      action: string;
      creditsCol: string;
      pagesLower: string;
      charsLower: string;
      tokensLower: string;
      statusUploaded: string;
      statusProcessing: string;
      statusOcrReview: string;
      statusTranslationReview: string;
      statusCompleted: string;
      allStatus: string;
      actionOrderCreated: string;
      actionOcrCompleted: string;
      actionOcrGenerated: string;
      actionOcrEdited: string;
      actionTranslationGen: string;
      actionTranslationEdit: string;
      actionDownload: string;
      actionProcessing: string;
      actionCompleted: string;
      actionTranslationReview: string;
      actionOcrReview: string;
      exportCSV: string;
      wordsLower: string;
      ordersLower: string;
      showingRangeTpl: string;
      orderLogsForTpl: string;
      visibilityAll: string;
      visibilityActive: string;
      visibilityTrash: string;
      visibilityDeleted: string;
      visibilityArchived: string;
      badgeInTrash: string;
      badgeDeleted: string;
      deletedAuto: string;
      byTpl: string;
      inTrashSince: string;
      statusCancelled: string;
      actorAuto: string;
      userCol: string;
      csvOrderNumber: string;
      csvDocumentName: string;
      csvAccount: string;
      csvStatus: string;
      csvPages: string;
      csvCreditsEstimated: string;
      csvCreditsConsumed: string;
      csvSourceLanguage: string;
      csvTargetLanguage: string;
      csvOrderDate: string;
      csvLogTimestamp: string;
      csvLogAction: string;
      csvLogCredits: string;
      csvLogUser: string;
      csvTrashedAt: string;
      csvDeletedAt: string;
      csvDeletedReason: string;
    };
    waitlist: {
      title: string;
      subtitle: string;
      totalSignups: string;
      companies: string;
      wantBetaAccess: string;
      latestSignup: string;
      searchPlaceholder: string;
      noEntriesFound: string;
      name: string;
      email: string;
      phone: string;
      company: string;
      role: string;
      pagesPerWeek: string;
      biggestPain: string;
      priceWillingness: string;
      betaInterest: string;
      language: string;
      signedUp: string;
      signedUpAt: string;
      deleteEntry: string;
      viewDetails: string;
      deleteConfirm: string;
      csvName: string;
      csvEmail: string;
      csvPhone: string;
      csvCompany: string;
      csvRole: string;
      csvPagesWeek: string;
      csvBiggestPain: string;
      csvPriceWillingness: string;
      csvBetaInterest: string;
      csvLanguage: string;
      csvDate: string;
    };
    analytics: {
      title: string;
      subtitle: string;
      totalOrders: string;
      activeAccounts: string;
      ordersProcessed: string;
      creditsConsumed: string;
      ordersByStatus: string;
      topLanguagePairs: string;
      documentsByType: string;
      pagesByDocType: string;
      imageBased: string;
      textBased: string;
      imagePages: string;
      textPages: string;
      documents: string;
      pages: string;
      noOrderData: string;
      noLanguagePairData: string;
      noDocData: string;
      noPageData: string;
      statusUploaded: string;
      statusProcessing: string;
      statusOcrReview: string;
      statusTranslationReview: string;
      statusCompleted: string;
      manuscript: string;
    };
    blog: {
      title: string;
      subtitle: string;
      searchPlaceholder: string;
      allStatuses: string;
      statusDraft: string;
      statusScheduled: string;
      statusPublished: string;
      statusArchived: string;
      noArticles: string;
      titleCol: string;
      status: string;
      locale: string;
      author: string;
      publishedAt: string;
      actions: string;
      viewOnSite: string;
      editSeo: string;
      deleteArticle: string;
      deleteConfirm: string;
      articles: string;
      pageOf: string;
      errorSave: string;
      loadFailed: string;
      savedOk: string;
      notFound: string;
      back: string;
      backToArticles: string;
      sectionBasic: string;
      slugHint: string;
      fieldExcerpt: string;
      excerptHint: string;
      sectionSeo: string;
      schemasHint: string;
      sectionFeaturedImage: string;
      altText: string;
      sectionPublication: string;
      fieldPublishDate: string;
      sectionInfo: string;
      viewPublished: string;
    };
    copy: {
      title: string;
      subtitle: string;
      discardDraft: string;
      saveDraft: string;
      publish: string;
      publishing: string;
      draftSaved: string;
      publishedMsg: string;
      errorSaveDraft: string;
      errorPublish: string;
      discardConfirm: string;
      draftDiscarded: string;
      loadingError: string;
      unpublishedChanges: string;
      pressPublish: string;
      unsaved: string;
      draftBadge: string;
      pendingChanges: string;
    };
    nav: {
      title: string;
      subtitle: string;
      newItem: string;
      noItems: string;
      dragHint: string;
      itemCreated: string;
      itemUpdated: string;
      saveError: string;
      deleteConfirm: string;
      newItemTitle: string;
      editItemTitle: string;
      kind: string;
      kindLink: string;
      kindLanding: string;
      kindDropdown: string;
      url: string;
      urlHint: string;
      landingPage: string;
      landingHint: string;
      selectPlaceholder: string;
      parentItem: string;
      parentHint: string;
      noParent: string;
      activeLabel: string;
      addSubItem: string;
      dragReorder: string;
      deleteItem: string;
      inactive: string;
      items: string;
      kindLinkShort: string;
      kindLandingShort: string;
      kindDropdownShort: string;
      itemsOne: string;
      itemsOther: string;
    };
    seo: {
      title: string;
      subtitle: string;
      tabGeneral: string;
      tabRobots: string;
      tabSitemap: string;
      tabPages: string;
      errorLoading: string;
      settingsSaved: string;
      errorSaving: string;
      saveChanges: string;
      addSocialProfile: string;
      // General tab
      generalSection: string;
      siteName: string;
      siteNamePlaceholder: string;
      siteUrl: string;
      siteUrlPlaceholder: string;
      titleTemplate: string;
      titleTemplateHint: string;
      titleTemplatePlaceholder: string;
      defaultTitle: string;
      titleCounterLabel: string;
      defaultDescription: string;
      descriptionCounterLabel: string;
      ogImage: string;
      ogImageHint: string;
      ogImagePlaceholder: string;
      googleVerification: string;
      googleVerificationPlaceholder: string;
      socialProfiles: string;
      socialProfilePlaceholder: string;
      googlePreview: string;
      socialPreview: string;
      // Robots tab
      indexingSection: string;
      allowGoogleIndex: string;
      allowGoogleIndexHint: string;
      blockedPathsSection: string;
      blockedPathsHint: string;
      blockedPathPlaceholder: string;
      aiPolicySection: string;
      aiPolicyAllowAll: string;
      aiPolicyAllowAllDesc: string;
      aiPolicyBlockTraining: string;
      aiPolicyBlockTrainingDesc: string;
      aiPolicyBlockAll: string;
      aiPolicyBlockAllDesc: string;
      robotsPreviewSection: string;
      // Sitemap tab
      sitemapSection: string;
      autoGenerateSitemap: string;
      autoGenerateSitemapHint: string;
      sitemapUpdated: string;
      sitemapUrlCopied: string;
      includedContentSection: string;
      includedStaticPages: string;
      includedLandingPages: string;
      includedBlogArticles: string;
      excludedHint: string;
      // Pages tab
      metaBasicSection: string;
      metaTitleLabelTpl: string;
      metaDescriptionLabelTpl: string;
      focusKeyword: string;
      canonicalUrl: string;
      canonicalPlaceholder: string;
      noindexLabel: string;
      nofollowLabel: string;
      openGraphSection: string;
      ogTitle: string;
      ogDescription: string;
      ogImageUrl: string;
      ogImageUrlPlaceholder: string;
      twitterSection: string;
      twitterCard: string;
      twitterSite: string;
      twitterSitePlaceholder: string;
      twitterTitle: string;
      twitterDescription: string;
      twitterImage: string;
      jsonLdSection: string;
      jsonLdLabel: string;
      jsonLdHint: string;
      jsonLdInvalid: string;
      changesPublished: string;
      previewGoogle: string;
      previewOg: string;
      infoSection: string;
      pageKeyLabel: string;
      lastEdited: string;
    };
    apiKeys: {
      title: string;
      subtitle: string;
      noKeys: string;
      webhookDeleted: string;
      webhookSaved: string;
      saveError: string;
      copiedToClipboard: string;
      statusActive: string;
      statusRevoked: string;
      webhookBadge: string;
      rateLimitTpl: string;
      lastUsedTpl: string;
      lastUsedNever: string;
      lastDeliveryTpl: string;
      noDeliveriesYet: string;
      editWebhook: string;
      configureWebhook: string;
      webhookUrlLabel: string;
      webhookUrlPlaceholder: string;
      webhookUrlHint: string;
      secretLabel: string;
      secretPlaceholder: string;
      secretHint: string;
      showSecret: string;
      hideSecret: string;
      saveSecretWarning: string;
      deleteWebhook: string;
      cancel: string;
      save: string;
      payloadExample: string;
    };
    landingPages: {
      title: string;
      subtitle: string;
      newPage: string;
      noPages: string;
      createFirst: string;
      deleteConfirm: string;
      totalLbl: string;
      activeLbl: string;
      draftsLbl: string;
      titleCol: string;
      slug: string;
      status: string;
      updated: string;
      actions: string;
      activeBadge: string;
      draftBadge: string;
      viewSite: string;
      editPage: string;
      deletePage: string;
      slugTitleRequired: string;
      errorCreate: string;
      errorSave: string;
      errorPublish: string;
      draftSavedMsg: string;
      publishedMsg: string;
      draftDiscarded: string;
      draftUnpublished: string;
      errorCreatePage: string;
      errorSaveDraft: string;
      notFound: string;
      discardConfirm: string;
      confirmUnpublish: string;
      confirmPublishTpl: string;
      unpublishedMsg: string;
      visibleMsg: string;
      newTitle: string;
      editTitle: string;
      badgePublished: string;
      badgeUnpublished: string;
      publicUrl: string;
      toggleTitleUnpublish: string;
      toggleTitlePublish: string;
      unpublish: string;
      publishPage: string;
      discardDraft: string;
      preview: string;
      createPage: string;
      saveDraft: string;
      publish: string;
      editingDraftTpl: string;
      sectionBasic: string;
      slugLockedHint: string;
      slugUrlHintTpl: string;
      fieldTitle: string;
      fieldPublishedFrom: string;
      publishedFromHint: string;
      fieldExpires: string;
      expiresHint: string;
      sectionContent: string;
      sectionSeo: string;
      fieldOgImage: string;
      ogImageHint: string;
      ogImagePlaceholder: string;
      previewGoogle: string;
      previewOg: string;
    };
    templates: {
      title: string;
      subtitle: string;
      noTemplates: string;
      uploadTemplate: string;
      newTemplate: string;
      editTemplate: string;
      activateTemplate: string;
      deactivateTemplate: string;
      default: string;
      global: string;
      custom: string;
      uploadDocx: string;
      globalTemplatesStat: string;
      activeStat: string;
      defaultStat: string;
      inactiveStat: string;
      categoryGeneral: string;
      categoryLegal: string;
      categoryImmigration: string;
      categoryAcademic: string;
      allTemplatesFilter: string;
      colTemplate: string;
      colCategory: string;
      colAccount: string;
      colFlags: string;
      colCreated: string;
      colActions: string;
      colActive: string;
      loadingTemplates: string;
      noTemplatesFound: string;
      flagDefault: string;
      flagGlobal: string;
      editBtn: string;
      newGlobalTemplate: string;
      uploadTemplateDialog: string;
      nameLabel: string;
      namePlaceholder: string;
      descriptionLabel: string;
      descriptionPlaceholder: string;
      categoryLabel: string;
      languageLabel: string;
      anyLanguage: string;
      languageEnglish: string;
      languageSpanish: string;
      languagePortuguese: string;
      cancelBtn: string;
      createAndEdit: string;
      creatingBtn: string;
      editingGlobalTemplate: string;
      variablesBtn: string;
      clickToCopyHint: string;
      copiedTooltip: string;
      clickToCopyTooltip: string;
      saveAndClose: string;
      savingBtn: string;
      variableTranslatorName: string;
      variableTranslatorEmail: string;
      variableCertificationDate: string;
      variableOrderDate: string;
      variableCompletionDate: string;
      variableSourceLanguage: string;
      variableTargetLanguage: string;
      variableSourceCountry: string;
      variableTargetCountry: string;
      variableDocumentTitle: string;
      variableDocumentType: string;
      variablePageCount: string;
      variableOrderNumber: string;
      variableProjectNumber: string;
      failedToPrepareTpl: string;
      failedToSaveTpl: string;
      failedToSaveGeneric: string;
      failedToCreate: string;
    };
    accountDetail: {
      loadFailed: string;
      loadDetailsFailed: string;
      notFound: string;
      backToAccounts: string;
      subtitle: string;
      typeFreelance: string;
      typeCompany: string;
      trialEndsTpl: string;
      legalName: string;
      taxId: string;
      website: string;
      yes: string;
      no: string;
      dateFormat: string;
      retention: string;
      noAutoPurge: string;
      retentionDaysTpl: string;
      orders: string;
      completed: string;
      inProgress: string;
      withFormat: string;
      recentOrders: string;
      colOrder: string;
      colDocument: string;
      colLanguages: string;
      colFormat: string;
      colPages: string;
      colCredits: string;
      colStatus: string;
      colCreated: string;
      noOrders: string;
      ordersUnavailable: string;
      spentToday: string;
      spentThisMonth: string;
      recentTransactions: string;
      balanceAfterTpl: string;
      noTransactions: string;
      txPurchase: string;
      txConsumption: string;
      txRefund: string;
      txAdjustment: string;
      txAutoRecharge: string;
      txSubscription: string;
      txCampaignBonus: string;
      txReferral: string;
      txClawback: string;
      teamMembers: string;
      firstName: string;
      lastName: string;
      activeRelTpl: string;
      lastLoginRelTpl: string;
      neverLoggedIn: string;
      pendingInvitations: string;
      pending: string;
      expiresTpl: string;
    };
    userDetail: {
      loadFailed: string;
      loadDetailsFailed: string;
      notFound: string;
      backToUsers: string;
      noAccounts: string;
      ordersOne: string;
      ordersOther: string;
      noOrders: string;
    };
    plans: {
      title: string;
      popular: string;
      saved: string;
      monthlyPrice: string;
      yearlyPrice: string;
      creditsMonth: string;
      creditsYear: string;
      yearlyDiscount: string;
      buttonText: string;
      yes: string;
      no: string;
      active: string;
      inactive: string;
      description: string;
      descriptionPlaceholder: string;
      limitsTitle: string;
      limitMaxCorpora: string;
      limitMaxMembers: string;
      limitHistoryDays: string;
      limitRetentionDays: string;
      featuresTitle: string;
      featureHumanVerifiedSeal: string;
      featureManuscripts: string;
      featureConfidential: string;
      featurePostEdit: string;
      featureOtp: string;
      featureApi: string;
      featureSso: string;
      featureWhiteLabel: string;
      benefits: string;
      moreTpl: string;
    };
    settingsBcc: {
      supportPlaceholder: string;
      eventPlaceholder: string;
      salesPlaceholder: string;
    };
    features: {
      title: string;
      subtitle: string;
      loadFailed: string;
      retry: string;
      totalFeatures: string;
      enabled: string;
      disabled: string;
      categories: string;
      searchPlaceholder: string;
      all: string;
      catCore: string;
      catTranslation: string;
      catBilling: string;
      catNotifications: string;
      catIntegrations: string;
      warning: string;
      empty: string;
      seed: string;
      noMatch: string;
    };
    hubspot: {
      subtitle: string;
      loadFailed: string;
      actionFailed: string;
      retryFailed: string;
      syncAll: string;
      confirmSyncAll: string;
      notConfiguredTpl: string;
      pending: string;
      processing: string;
      done: string;
      failed: string;
      contactsSynced: string;
      companiesSynced: string;
      recentFailures: string;
      colObject: string;
      colId: string;
      colAttempts: string;
      colLastError: string;
      colDate: string;
      noFailures: string;
    };
    knowledge: {
      title: string;
      subtitleTpl: string;
      newArticle: string;
      loadFailed: string;
      searchPlaceholder: string;
      emptyNone: string;
      emptyNoMatch: string;
      createFirst: string;
      colArticle: string;
      colKey: string;
      colSection: string;
      colLanguages: string;
      colStatus: string;
      colUpdated: string;
      colActions: string;
      edit: string;
      deleteAll: string;
      confirmDeleteTpl: string;
      published: string;
      draft: string;
      missingTranslation: string;
      allPublished: string;
      publishedCountTpl: string;
      titleRequired: string;
      saveFailed: string;
      publishFailed: string;
      confirmUnpublish: string;
      back: string;
      titlePlaceholder: string;
      titleShortPlaceholder: string;
      savedTpl: string;
      view: string;
      saveDraft: string;
      unpublish: string;
      republish: string;
      publish: string;
      fieldSection: string;
      noSection: string;
      fieldCategory: string;
      categoryPlaceholder: string;
      fieldLanguage: string;
      editorPlaceholder: string;
      editorTipTpl: string;
      featureOrderCreate: string;
      featureOrderDetail: string;
      featureOrderList: string;
      featureCreditsBilling: string;
      featureTeamManagement: string;
      featureCertificate: string;
      featureGlossary: string;
      featureAccountSettings: string;
      featureFaq: string;
      groupLabel: string;
      keyLabel: string;
      publishedWithDraft: string;
      missingShort: string;
      missingVersionTpl: string;
      creating: string;
      createTranslationTpl: string;
      createFailed: string;
      featureReleaseNotes: string;
      articleLoadFailed: string;
    };
    marketingUi: {
      charsTpl: string;
      idealTpl: string;
      noOgImage: string;
      noTitle: string;
      noDescription: string;
      previewTitle: string;
      noContentYet: string;
      previewFooter: string;
      close: string;
      imageTooLargeTpl: string;
      uploadFailedTpl: string;
      uploadTimeout: string;
      uploadError: string;
      uploadImageTpl: string;
      upload: string;
      uploading: string;
      clear: string;
      imageLoaded: string;
      previewAlt: string;
      editorPlaceholder: string;
      loadingEditor: string;
      linkPrompt: string;
      youtubePrompt: string;
      editorUploadFailedTpl: string;
      editorUploadTimeout: string;
      bold: string;
      italic: string;
      strike: string;
      inlineCode: string;
      heading1: string;
      heading2: string;
      heading3: string;
      bulletList: string;
      orderedList: string;
      quote: string;
      divider: string;
      link: string;
      uploadImageCloud: string;
      insertYoutube: string;
      imageLabel: string;
      alignLeft: string;
      alignCenter: string;
      alignRight: string;
      uploadingImage: string;
      undo: string;
      redo: string;
    };
    apiErrors: {
      fieldNotAllowedTpl: string;
      mustBeTextTpl: string;
      mustBeBooleanTpl: string;
      mustBeTypeTpl: string;
      requiredTpl: string;
      minLengthTpl: string;
      maxLengthTpl: string;
      upstream: string;
      notFound: string;
      tooLarge: string;
      unauthorized: string;
      forbidden: string;
      invalidLocale: string;
      fieldSlug: string;
      fieldTitle: string;
      fieldContent: string;
      fieldOgImage: string;
      fieldPublishedAt: string;
      fieldExpiresAt: string;
      fieldIsActive: string;
      fieldKind: string;
      /** Lleva {lang}: ES, EN, PT… */
      fieldLabelTpl: string;
      /** La landing ya existe con ese slug (marketing-ms: `slug_taken`). */
      slugTaken: string;
      /** El slug choca con una página pública de la app (`reserved_slug`). */
      slugReserved: string;
    };
    copyFields: {
      secHero: string;
      secFeatures: string;
      secFeatureCardTpl: string;
      secFaq: string;
      secFaqItemTpl: string;
      secPricing: string;
      secCtaFinal: string;
      secFooter: string;
      titleLine1: string;
      titleLine2: string;
      titleSizeDesktop: string;
      titleSizeMobile: string;
      titleSizeDesktopHint: string;
      titleSizeMobileHint: string;
      subtitleSentence1: string;
      subtitleSentence2: string;
      ctaPrimary: string;
      ctaSecondary: string;
      ctaDemo: string;
      ctaDemoUrl: string;
      title: string;
      subtitle: string;
      name: string;
      description: string;
      descriptionHover: string;
      question: string;
      answer: string;
      titlePart1: string;
      titlePart2: string;
      button: string;
      tagline: string;
      auto: string;
    };
    logs: {
      title: string;
      subtitle: string;
      totalLogs: string;
      documents: string;
      translations: string;
      searchPlaceholder: string;
      allActions: string;
      filterStatusCompleted: string;
      actionAccountStatus: string;
      actionAccountStatusChange: string;
      actionMemberUpdate: string;
      actionFeatureToggle: string;
      actionSettingsUpdate: string;
      colOrder: string;
      colDetails: string;
      noLogs: string;
      loadFailed: string;
      pageOfTpl: string;
      totalTpl: string;
      csvDate: string;
    };
    webinar: {
      titleTpl: string;
      subtitleTpl: string;
      reload: string;
      totalParticipants: string;
      language: string;
      latestSignup: string;
      searchPlaceholder: string;
      empty: string;
      loadFailed: string;
      colEmail: string;
      colSource: string;
      colDate: string;
      csvCreatedAt: string;
    };
    support: {
      title: string;
      subtitle: string;
      searchPlaceholder: string;
      anyPriority: string;
      statusOpen: string;
      statusPending: string;
      statusSolved: string;
      statusSnoozed: string;
      statusSpam: string;
      priorityLow: string;
      priorityNormal: string;
      priorityHigh: string;
      priorityUrgent: string;
      channelEmail: string;
      channelChat: string;
      channelApi: string;
      channelForm: string;
      channelSlack: string;
      sentimentPositive: string;
      sentimentNegative: string;
      sentimentNeutral: string;
      sentimentAngry: string;
      noTickets: string;
      colSubject: string;
      colChannel: string;
      colPriority: string;
      colStatus: string;
      colSentiment: string;
      colCreated: string;
      noSubject: string;
      pageTpl: string;
      connectError: string;
      backToInbox: string;
      ticketNotFound: string;
      ticketLoadError: string;
      timeNow: string;
      timeMinTpl: string;
      timeHourTpl: string;
      timeDayTpl: string;
      createdTpl: string;
      hideContext: string;
      showContext: string;
      aiSummary: string;
      internalNotePlaceholder: string;
      replyPlaceholder: string;
      send: string;
      internalNote: string;
      internalNoteNoEmail: string;
      uncheckNoEmail: string;
      notifyByEmail: string;
      enterSendsTpl: string;
      ticketContext: string;
      closePanel: string;
      suggestedDraft: string;
      generatedByAi: string;
      aiBadge: string;
      useReply: string;
      copy: string;
      discard: string;
      customer: string;
      agent: string;
      aiAssistant: string;
      internalBadge: string;
      ezdocuCustomer: string;
      anonymousSender: string;
      copyUserId: string;
      userSessionsPosthog: string;
      userSessionsPosthogTitle: string;
      visitorBadge: string;
      lastActivity: string;
      lastLogin: string;
      lastPage: string;
      timezone: string;
      signupDate: string;
      purchasesTitle: string;
      statTotal: string;
      statCompleted: string;
      statInProgress: string;
      statCredits: string;
      accountName: string;
      legalName: string;
      taxId: string;
      website: string;
      accountStatus: string;
      yes: string;
      no: string;
      dateFormat: string;
      accountCreated: string;
      owner: string;
      accountDetails: string;
      membersTitle: string;
      noMembers: string;
      inactive: string;
      pending: string;
      aiAssist: string;
      intentTpl: string;
      suggestedTags: string;
      sessionReplay: string;
      sessionReplayDesc: string;
      openPosthog: string;
      relatedOrder: string;
      goToOrder: string;
      relatedDocument: string;
      openDocument: string;
      unassigned: string;
      detailTags: string;
      noTags: string;
      detailLanguage: string;
      detailFirstResponse: string;
      detailResolved: string;
      detailSlaBreached: string;
      pastConversations: string;
      firstTime: string;
      activityLog: string;
      noEvents: string;
      relToday: string;
      relDaysTpl: string;
      relWeeksTpl: string;
      relMonthsTpl: string;
      actorSystem: string;
      actorSomeone: string;
      actCreated: string;
      actAssigned: string;
      actUnassigned: string;
      actStatusChanged: string;
      actPriorityChanged: string;
      actTagAdded: string;
      actTagRemoved: string;
      actReplySent: string;
      actNoteAdded: string;
      actResolved: string;
      actReopened: string;
      actSnoozed: string;
      actMerged: string;
      viewWidgetConversation: string;
      widgetConversation: string;
      messagesOne: string;
      messagesOther: string;
      attachedImage: string;
      attachment: string;
      attachedFile: string;
      fileFallback: string;
      loading: string;
    };
  };
  // Admin → Pagos. Plantillas con {n}, {total}, {date}, {net}, {refunds}, {usd}.
  adminPayments: {
    title: string;
    transactionsOne: string;
    transactionsOther: string;
    subtitle: string;
    refresh: string;
    exportExcel: string;
    exporting: string;
    exportError: string;
    searchPlaceholder: string;
    countOf: string;
    loading: string;
    empty: string;
    emptyFiltered: string;
    viewAccount: string;
    staleBackend: string;
    filters: {
      period: string;
      type: string;
      status: string;
      from: string;
      to: string;
      clear: string;
      sinceDate: string;
      untilDate: string;
    };
    periods: {
      all: string;
      thisMonth: string;
      lastMonth: string;
      last30Days: string;
      thisYear: string;
      custom: string;
    };
    categories: {
      all: string;
      plan: string;
      credits: string;
      autoRecharge: string;
      refund: string;
    };
    statusAll: string;
    statuses: {
      active: string;
      trial: string;
      cancelsAtEnd: string;
      canceled: string;
      unpaid: string;
      incomplete: string;
      unknown: string;
      paid: string;
      refund: string;
    };
    statusHelp: {
      active: string;
      cancelsAtEnd: string;
      unpaid: string;
      unknown: string;
    };
    cycles: {
      yearly: string;
      monthly: string;
    };
    items: {
      plan: string;
      creditPack: string;
      autoRecharge: string;
      refund: string;
    };
    cards: {
      revenue: string;
      total: string;
      netAndRefunds: string;
      customersOne: string;
      customersOther: string;
      thisMonth: string;
      plans: string;
      paymentsOne: string;
      paymentsOther: string;
      creditPacks: string;
      purchasesOne: string;
      purchasesOther: string;
      activeSubscriptions: string;
      today: string;
      mrr: string;
      cancelling: string;
      unpaid: string;
      unknown: string;
      creditsSold: string;
    };
    columns: {
      date: string;
      customer: string;
      account: string;
      item: string;
      cycle: string;
      status: string;
      nextCharge: string;
      description: string;
      credits: string;
      amount: string;
      stripeId: string;
      mode: string;
    };
    excel: {
      sheet: string;
      date: string;
      customer: string;
      email: string;
      account: string;
      type: string;
      cycle: string;
      status: string;
      nextCharge: string;
      description: string;
      credits: string;
      amountUsd: string;
      stripeId: string;
      mode: string;
    };
  };
  // Admin → Panel de administración (Resumen, Costos, Analíticas). Plantillas con
  // {n}, {usd}, {pct}, {range}, {time}, {env}, {envs}, {reason}, {parts}…
  adminPanel: {
    title: string;
    subtitle: string;
    refresh: string;
    retry: string;
    exportExcel: string;
    exporting: string;
    exportError: string;
    exportEmpty: string;
    updatedAt: string;
    tabs: {
      resumen: string;
      costos: string;
      analiticas: string;
    };
    periods: {
      today: string;
      thisWeek: string;
      thisMonth: string;
      lastMonth: string;
      last30: string;
      last90: string;
      custom: string;
    };
    filters: {
      period: string;
      from: string;
      to: string;
      apply: string;
      rangeIncomplete: string;
      rangeTooLong: string;
      compare: string;
      payments: string;
      paymentsHint: string;
      live: string;
      test: string;
    };
    envs: {
      summing: string;
      notDeployed: string;
      notConfigured: string;
      error: string;
      othersShown: string;
      noData: string;
    };
    parts: {
      unavailable: string;
      missingTable: string;
      missingTableIn: string;
      error: string;
      errorIn: string;
      partial: string;
    };
    kpis: {
      revenue: string;
      revenueTest: string;
      revenueHint: string;
      salesCount: string;
      refunds: string;
      showSales: string;
      hideSales: string;
      onlyTestPayments: string;
      orders: string;
      completedInPeriod: string;
      pages: string;
      creditsUsed: string;
      activeAccounts: string;
      activeAccountsHint: string;
      newAccounts: string;
      newAccountsHint: string;
      vsPrevious: string;
      more: string;
      less: string;
      noPrevious: string;
    };
    charts: {
      byDay: string;
      byDayOrdersHint: string;
      byDayRevenueHint: string;
      metric: string;
      metrics: {
        ordenes: string;
        hojas: string;
        ingresos: string;
      };
      completed: string;
      inProgress: string;
      notStarted: string;
      total: string;
      totalCost: string;
      previousPeriod: string;
      previousNoInfra: string;
      byEnvironment: string;
    };
    funnel: {
      title: string;
      hint: string;
      created: string;
      ocr: string;
      translation: string;
      completed: string;
      medianFromUpload: string;
      medianFromOcr: string;
      medianFromTranslation: string;
      totalMedian: string;
      byEnvironment: string;
    };
    pairs: {
      title: string;
      hint: string;
      others: string;
    };
    stuck: {
      title: string;
      hint: string;
      kpiHint: string;
      none: string;
      since: string;
    };
    topAccounts: {
      title: string;
      hint: string;
      account: string;
      pages: string;
      orders: string;
      revenue: string;
      revenueTest: string;
      noName: string;
    };
    costs: {
      total: string;
      ai: string;
      ocr: string;
      infra: string;
      infraLoading: string;
      noInfra: string;
      margin: string;
      marginPct: string;
      netRevenue: string;
      noRevenue: string;
      perPage: string;
      perOrder: string;
      pagesOrders: string;
      revenuePerPage: string;
      revenuePerPageHint: string;
      creditPricePeriod: string;
      creditPrice90d: string;
      noCreditPrice: string;
      incomplete: string;
      parts: {
        ia: string;
        noLlm: string;
        infra: string;
      };
      infraNotDeployed: string;
      infraPartial: string;
      sharedTag: string;
      sharedPartTag: string;
      sharedPart: string;
      sharedIncluded: string;
      sharedNote: string;
      byDay: string;
      byDayHint: string;
      byCategory: string;
      byCategoryHint: string;
      categories: {
        claude: string;
        gemini: string;
        otherAi: string;
        ocr: string;
        railway: string;
        vercel: string;
        aws: string;
      };
      aiDetail: string;
      aiDetailHint: string;
      infraDetail: string;
      infraDetailHint: string;
      seeDetail: string;
    };
    unit: {
      title: string;
      hint: string;
      type: string;
      origin: string;
      orders: string;
      pages: string;
      aiPerPage: string;
      revenuePerPage: string;
      marginPerPage: string;
      note: string;
    };
    delivery: {
      median: string;
      p90: string;
      p90Hint: string;
      onTime: string;
      onTimeDetail: string;
      completedN: string;
      perEnvBelow: string;
      envLine: string;
      byType: string;
      byPair: string;
      completed: string;
      medianShort: string;
      p90Short: string;
      onTimeShort: string;
      percentilesNote: string;
    };
    quality: {
      title: string;
      hint: string;
      aiFailures: string;
      aiFailuresDetail: string;
      aiCalls: string;
      ok: string;
      failed: string;
      aiFootnote: string;
      api: string;
      noApi: string;
      webFailuresMissing: string;
    };
    usage: {
      title: string;
      hint: string;
      origin: string;
      mode: string;
      modeTranslate: string;
      modeOther: string;
      method: string;
      methods: {
        option1: string;
        option2: string;
        corpus: string;
        other: string;
      };
      format: string;
      noFormat: string;
      certificate: string;
      verified: string;
      signature: string;
      yes: string;
      no: string;
    };
    docs: {
      title: string;
      hint: string;
      ordersByType: string;
      pagesByType: string;
      byCategory: string;
    };
    retention: {
      title: string;
      hint: string;
      columns: {
        cohort: string;
        accounts: string;
        m1: string;
        m2: string;
        m3: string;
      };
      lost: string;
      lostHint: string;
      lostCount: string;
      noneLost: string;
      ordersBefore: string;
    };
    accounts: {
      title: string;
      hint: string;
      byPlan: string;
      byStatus: string;
      noPlan: string;
      credits: string;
    };
    assistant: {
      title: string;
      hint: string;
      cta: string;
    };
    states: {
      empty: string;
      emptyHint: string;
    };
    status: {
      uploaded: string;
      processing: string;
      ocrreview: string;
      translationreview: string;
      completed: string;
    };
    docTypes: {
      image: string;
      text: string;
      handwritten: string;
      desconocido: string;
    };
    categories: {
      legal: string;
      immigration: string;
      academic: string;
      general: string;
      ninguna: string;
    };
    origins: {
      web: string;
      api: string;
    };
    apiStates: {
      pendiente: string;
      en_curso: string;
      esperando_revision: string;
      sin_creditos: string;
      terminado: string;
      fallido: string;
      cancelado: string;
    };
    accountStatus: {
      trial: string;
      active: string;
      suspended: string;
      inactive: string;
      desconocido: string;
    };
    excel: {
      sheets: {
        resumen: string;
        costos: string;
        analiticas: string;
      };
      date: string;
      env: string;
      orders: string;
      completed: string;
      inProgress: string;
      notStarted: string;
      pages: string;
      revenue: string;
      revenueTest: string;
      claude: string;
      gemini: string;
      otherAi: string;
      ocrDeepl: string;
      railway: string;
      vercel: string;
      aws: string;
      infraRow: string;
      infraSharedRow: string;
      totalCost: string;
      group: string;
      groupTotal: string;
      groupType: string;
      groupPair: string;
      key: string;
      medianHours: string;
      p90Hours: string;
      onTimePct: string;
    };
  };
  // Admin → Costos de IA. Plantillas con {n}, {usd}, {date}, {pct}, {range},
  // {env}, {time}, {reason}, {code}. Los nombres de modelo no se traducen.
  // Admin → Costos de infraestructura (Railway, Vercel, AWS). Plantillas con {n},
  // {usd}, {pct}, {range}, {time}, {vars}, {date}, {service}, {tenant}, {reason}.
  adminInfraCosts: {
    title: string;
    subtitle: string;
    refresh: string;
    exportExcel: string;
    exporting: string;
    exportError: string;
    exportTruncated: string;
    exportEmpty: string;
    updatedAt: string;
    providers: {
      railway: string;
      vercel: string;
      aws: string;
    };
    envs: {
      production: string;
      staging: string;
      qa: string;
      compartido: string;
    };
    /** Unidades de uso que mandan los proveedores, en el idioma del panel. */
    units: {
      minute: string;
      hour: string;
      gigabyte: string;
      gigabyteHour: string;
      invocations: string;
      reads: string;
      writes: string;
      seats: string;
      transformations: string;
      pages: string;
      gb: string;
      gbMin: string;
      vcpuMin: string;
    };
    projects: {
      ezdocu: string;
      callcenter: string;
      frontend: string;
      vercelTeam: string;
      aws: string;
    };
    filters: {
      period: string;
      provider: string;
      allProviders: string;
      environment: string;
      allEnvironments: string;
      from: string;
      to: string;
      apply: string;
      rangeTooLong: string;
      rangeIncomplete: string;
    };
    periods: {
      today: string;
      last7: string;
      thisMonth: string;
      lastMonth: string;
      last30: string;
      last90: string;
      custom: string;
    };
    kpis: {
      total: string;
      ofTotal: string;
      vsPrevious: string;
      more: string;
      less: string;
      noPrevious: string;
      estimated: string;
      perDay: string;
    };
    status: {
      ok: string;
      notConnected: string;
      notConnectedHint: string;
      error: string;
      errorNever: string;
      collecting: string;
      staleHere: string;
      estimated: string;
    };
    charts: {
      byDay: string;
      byDayHint: string;
      total: string;
    };
    tables: {
      byService: string;
      byServiceHint: string;
      provider: string;
      project: string;
      service: string;
      usd: string;
      share: string;
      quantity: string;
      environments: string;
      showAll: string;
      showLess: string;
    };
    byEnv: {
      title: string;
      hint: string;
    };
    callcenter: {
      title: string;
      hint: string;
      total: string;
      empty: string;
    };
    findings: {
      title: string;
      biggestService: string;
      nonProduction: string;
      vercelBuilds: string;
    };
    notes: {
      title: string;
      railwayRate: string;
      callcenterShared: string;
      vercelNoEnv: string;
      vercelTeam: string;
      awsNoEnv: string;
      awsSharedAccount: string;
      envFilter: string;
      railwayInvoice: string;
    };
    invoice: {
      title: string;
      hint: string;
      adjusted: string;
      partial: string;
      rateOnly: string;
      chipInvoice: string;
      chipRate: string;
      beforeAdjust: string;
      daysAdjusted: string;
      method: string;
      noTable: string;
      error: string;
      noPeriods: string;
      period: string;
      status: string;
      invoiceCol: string;
      rateCol: string;
      diff: string;
      ezdocu: string;
      callcenter: string;
      others: string;
      closed: string;
      current: string;
      partialCoverage: string;
      breakdown: string;
      credits: string;
      factor: string;
    };
    vercelCycle: {
      title: string;
      hint: string;
      current: string;
      inProgress: string;
      closed: string;
      includedCredit: string;
      includedOf: string;
      onDemand: string;
      usage: string;
      usageHint: string;
      frontend: string;
      subscriptions: string;
      measuredAt: string;
      periodTotal: string;
      period: string;
      billed: string;
      partial: string;
      noTable: string;
      error: string;
      noCycle: string;
      method: string;
    };
    states: {
      noTable: string;
      error: string;
      retry: string;
      empty: string;
      emptyHint: string;
    };
    excel: {
      sheet: string;
      day: string;
      provider: string;
      project: string;
      environment: string;
      service: string;
      measure: string;
      quantity: string;
      unit: string;
      usd: string;
      estimated: string;
      usdAdjusted: string;
      adjustment: string;
      adjustmentInvoice: string;
      adjustmentRate: string;
      yes: string;
      no: string;
    };
  };
  adminAiCosts: {
    title: string;
    subtitle: string;
    refresh: string;
    exportExcel: string;
    exporting: string;
    exportError: string;
    exportTruncated: string;
    exportEmpty: string;
    updatedAt: string;
    envSwitchLabel: string;
    envBadge: { production: string; staging: string; qa: string; local: string };
    envAll: string;
    multiEnv: {
      hint: string;
      summing: string;
      notRecording: string;
      notConfigured: string;
      noTable: string;
      error: string;
      exportPartial: string;
      othersShown: string;
      byEnvironment: string;
      noData: string;
    };
    filters: {
      period: string;
      provider: string;
      allProviders: string;
      from: string;
      to: string;
      apply: string;
      rangeTooLong: string;
      rangeIncomplete: string;
    };
    periods: {
      today: string;
      last7: string;
      thisMonth: string;
      lastMonth: string;
      last30: string;
      last90: string;
      custom: string;
    };
    kpis: {
      totalAi: string;
      platform: string;
      callcenter: string;
      claude: string;
      gemini: string;
      perPage: string;
      perOrder: string;
      ofTotal: string;
      callsOne: string;
      callsOther: string;
      pagesOne: string;
      pagesOther: string;
      ordersOne: string;
      ordersOther: string;
      vsPrevious: string;
      noPrevious: string;
      platformDeltaNote: string;
      savingsShort: string;
      more: string;
      less: string;
    };
    charts: {
      byDay: string;
      byDayHint: string;
      byProcess: string;
      byProcessHint: string;
      others: string;
      usd: string;
      calls: string;
      total: string;
    };
    savings: {
      title: string;
      note: string;
      geminiCost: string;
      claudeEstimate: string;
      savings: string;
      shadow: string;
      none: string;
      process: string;
    };
    tables: {
      byModel: string;
      byProcess: string;
      model: string;
      process: string;
      provider: string;
      calls: string;
      tokensIn: string;
      cacheRead: string;
      cacheWrite: string;
      cacheSplit: string;
      tokensOut: string;
      reasoning: string;
      avgSeconds: string;
      usd: string;
      share: string;
      failed: string;
      fallbacks: string;
      stages: string;
      models: string;
      providers: string;
      unknownPriceOne: string;
      unknownPriceOther: string;
    };
    top: {
      accounts: string;
      orders: string;
      account: string;
      order: string;
      ordersCount: string;
      pages: string;
      usdPerPage: string;
      noAccount: string;
      processes: string;
      empty: string;
    };
    environments: {
      title: string;
      hint: string;
      unavailable: string;
      notConfigured: string;
      noRecord: string;
      notRecording: string;
      otherRows: string;
      current: string;
    };
    callcenter: {
      title: string;
      subtitle: string;
      cost: string;
      conversations: string;
      messages: string;
      tickets: string;
      autoReplied: string;
      escalated: string;
      byDay: string;
      byProcessModel: string;
      noCostBefore: string;
      onlyProd: string;
      unavailable: string;
      empty: string;
      motivos: {
        'no-configurado': string;
        timeout: string;
        'no-autorizado': string;
        'rango-invalido': string;
        http: string;
        red: string;
        'respuesta-invalida': string;
      };
    };
    states: {
      loading: string;
      empty: string;
      emptyHint: string;
      noTable: string;
      error: string;
      retry: string;
      noRecordBefore: string;
      backfillNote: string;
      notConfigured: string;
      notRecording: string;
    };
    groups: {
      'crear-orden': string;
      procesar: string;
      traducir: string;
      revisar: string;
      glosario: string;
      formato: string;
      admin: string;
      latente: string;
    };
    processes: {
      clasificacion: string;
      'clasificacion-sombra': string;
      'giro-texto': string;
      'esquinas-foto': string;
      transcripcion: string;
      'marcas-bbox': string;
      'tablas-validador': string;
      'traduccion-html': string;
      'traduccion-terminos': string;
      'traduccion-textos': string;
      'post-edicion': string;
      'corpus-correcciones': string;
      'corpus-extractor': string;
      'glosario-validador': string;
      'glosario-ingesta': string;
      'glosario-metadatos': string;
      'cierre-formula': string;
      'plantilla-nombre': string;
      'insights-chat': string;
      'mensaje-compartir': string;
      'manuscrito-docx-legacy': string;
      'escaneo-foto-legacy': string;
      'fusion-parrafos': string;
      preanalisis: string;
      'preproceso-ocr': string;
      'legacy-colores': string;
      'legacy-tablas-hoja': string;
      'legacy-transcripcion-html': string;
      traduccion: string;
      'widget-chat': string;
      autoresponder: string;
      tagger: string;
      summary: string;
      embeddings: string;
    };
    stages: {
      principal: string;
      precalentado: string;
      antibucle: string;
      'reintento-corte': string;
      'sin-contexto': string;
      backfill: string;
    };
    providers: { anthropic: string; gemini: string; voyage: string; other: string };
    sources: { vivo: string; backfill: string };
    summaryCard: {
      title: string;
      seeDetail: string;
      platformOnly: string;
      noData: string;
    };
    excel: {
      sheet: string;
      date: string;
      environment: string;
      provider: string;
      model: string;
      process: string;
      processId: string;
      stage: string;
      calls: string;
      failed: string;
      fallbacks: string;
      shadow: string;
      tokensUncached: string;
      cacheRead: string;
      cacheWrite: string;
      tokensOut: string;
      reasoning: string;
      usd: string;
      avgSeconds: string;
      source: string;
      yes: string;
      no: string;
    };
  };
  glossary: {
    title: string;
    subtitle: string;
    categoriesBtn: string;
    uploadFile: string;
    newTerm: string;
    breadcrumbRoot: string;
    categoriesWithDataTpl: string;   // "Categories with terms ({n})"
    emptyCategoriesTpl: string;      // "Empty categories ({n})"
    termsCountOne: string;           // "{n} term"
    termsCountOther: string;         // "{n} terms"
    pairsCountOne: string;
    pairsCountOther: string;
    defsCountTpl: string;            // "{n} def."
    backToCategories: string;
    pairType: string;
    definitionType: string;
    pairTypeDescTpl: string;         // "Term + translation. {n} entries."
    definitionTypeDescTpl: string;
    backToTypes: string;
    langComboTermsTpl: string;       // "{n} terms · {src}{arrow}"
    emptyType: string;
    deleteLangTitle: string;
    deleteLangBodyTpl: string;       // "You will delete {n} terms from {cat} · {combo}."
    deleteCannotUndo: string;
    cancel: string;
    deleteCountTpl: string;          // "Delete {n}"
    backToLanguages: string;
    filterByLetter: string;
    allLetters: string;
    search: string;
    searchPlaceholder: string;
    onlyVerified: string;
    refresh: string;
    downloadPdf: string;
    generatingPdf: string;
    downloadPdfTipFiltered: string;
    downloadPdfTipAll: string;
    selectFilterCountTpl: string;    // "Select all {n} terms in filter"
    selectedCountTpl: string;        // "{n} selected"
    allFilter: string;
    verify: string;
    unverify: string;
    clear: string;
    pageXofYTpl: string;             // "page {p} / {t}"
    letterLabelTpl: string;          // "letter {l}"
    otherLetter: string;
    noTerms: string;
    previous: string;
    next: string;
    term: string;
    translation: string;
    definition: string;
    source: string;
    actions: string;
    verifiedTitle: string;
    unverifiedTitle: string;
    selectPage: string;
    entryNewTitle: string;
    entryEditTitle: string;
    fieldCategory: string;
    fieldCategoryHelp: string;
    fieldType: string;
    fieldTypeHelp: string;
    fieldSourceTerm: string;
    fieldSourceTermHelp: string;
    fieldLang: string;
    fieldSourceLangHelp: string;
    fieldTranslation: string;
    fieldTranslationHelp: string;
    fieldTargetLangHelp: string;
    fieldDefinition: string;
    fieldDefinitionHelp: string;
    fieldContext: string;
    fieldContextHelp: string;
    fieldPriority: string;
    fieldPriorityHelp: string;
    fieldVerified: string;
    fieldVerifiedHelp: string;
    fieldSourceKind: string;
    fieldSourceKindHelp: string;
    fieldSourceLabel: string;
    fieldSourceLabelHelp: string;
    fieldSourceUrl: string;
    fieldSourceUrlHelp: string;
    fieldSourceUrlHint: string;
    provenanceTitle: string;
    selectPlaceholder: string;
    entryPairOption: string;
    entryDefinitionOption: string;
    save: string;
    sourceTermPlaceholder: string;
    translationPlaceholder: string;
    definitionPlaceholder: string;
    contextPlaceholder: string;
    sourceLabelPlaceholder: string;
    originalFile: string;
    sourceManual: string;
    sourcePdf: string;
    sourceCsv: string;
    sourceTxt: string;
    sourceAi: string;
    sourceLearning: string;
    sourceWeb: string;
    sourceClient: string;
    uploadTitle: string;
    uploadConfirmTitle: string;
    tabFile: string;
    tabUrl: string;
    tabPaste: string;
    uploadClickToSelect: string;
    uploadHint: string;
    uploadAnalyzing: string;
    urlFieldLabel: string;
    urlFieldHint: string;
    analyzeUrl: string;
    readingPage: string;
    pageTitleLabel: string;
    pasteFieldLabel: string;
    pasteFieldHint: string;
    pastePlaceholder: string;
    sourceName: string;
    sourceNamePlaceholder: string;
    originalUrlOptional: string;
    analyzeText: string;
    analyzing: string;
    claudeHelpTitle: string;
    claudeHelpDetectLangs: string;
    claudeHelpDetectType: string;
    claudeHelpSuggestCategory: string;
    claudeHelpSaveSource: string;
    claudeAnalyzedTpl: string;       // "Claude analyzed {file} ({chars} chars). Review..."
    destCategory: string;
    contentType: string;
    pairOption: string;
    monolingualOption: string;
    srcLang: string;
    tgtLang: string;
    savedSource: string;
    changeSource: string;
    process: string;
    urlInvalid: string;
    urlEmpty: string;
    urlNoText: string;
    pasteTooShort: string;
    urlErrorGeneric: string;
    detectError: string;
    uploadErrorGeneric: string;
    confSure: string;
    confProbable: string;
    confUnsure: string;
    msgTermCreated: string;
    msgTermUpdated: string;
    msgTermDeleted: string;
    msgSaveError: string;
    msgBulkVerifiedTpl: string;      // "{n} terms verified"
    msgBulkUnverifiedTpl: string;
    msgVerifyError: string;
    msgBulkDeletedTpl: string;
    msgDeleteError: string;
    msgPdfDownloadedTpl: string;     // "PDF downloaded ({n} entries)"
    msgPdfError: string;
    errorFetchEntries: string;
    helpAria: string;
    langNameEs: string;
    langNameEn: string;
    langNamePt: string;
    langNameFr: string;
    langNameIt: string;
    langNameDe: string;
    fileReadError: string;
    urlReadErrorTpl: string;
    pasteLabelCsv: string;
    pasteLabelText: string;
    noContent: string;
    charsCountTpl: string;
    jobTitle: string;
    jobSuccess: string;
    jobResultTpl: string;
    jobTokensIn: string;
    jobTokensOut: string;
    jobCacheHit: string;
    jobCostUsd: string;
    jobMethodModelTpl: string;
    jobError: string;
    jobPreviewTpl: string;
    jobColSource: string;
    jobColTarget: string;
    jobShowingTpl: string;
    jobDiscard: string;
    jobConfirm: string;
    jobClose: string;
    stageQueued: string;
    stageExtracting: string;
    stageParsing: string;
    stageParsedTpl: string;
    stageAwaiting: string;
    stageInserting: string;
    stageDone: string;
    stageCancelled: string;
    stageError: string;
    catBackToGlossaries: string;
    catTitle: string;
    catSubtitle: string;
    catNew: string;
    catEdit: string;
    catCreated: string;
    catUpdated: string;
    catDeleted: string;
    catCannotDelete: string;
    catConfirmDeleteTpl: string;
    catShowInactive: string;
    catCountOne: string;
    catCountOther: string;
    catColKey: string;
    catColName: string;
    catColTerms: string;
    catColActive: string;
    catColActions: string;
    catSystemTitle: string;
    catSystemBadge: string;
    catActivate: string;
    catDeactivate: string;
    catEditBtn: string;
    catDeleteBtn: string;
    catEmpty: string;
    catFieldKey: string;
    catFieldKeyHint: string;
    catKeyPlaceholder: string;
    catFieldNameTpl: string;
    catFieldDescription: string;
    catFieldIcon: string;
    catFieldIconHint: string;
    catFieldOrder: string;
    catActiveLabel: string;
    catSave: string;
  };
  sharedComponents: {
    notificationBell: {
      ariaLabel: string;
    };
    languageSelector: {
      tooltip: string;
    };
    professionalProfile: {
      associationSearchHint: string;
      associationSearchPlaceholder: string;
      noResults: string;
    };
  };
  verifyPublic: {
    humanVerified: string;
    kmsSigned: string;
    translationOnRecord: string;
    statusHashing: string;
    statusPurgedWithHash: string;
    statusWithHash: string;
    statusNoHash: string;
    documentVerifiedTitle: string;
    documentVerifiedDesc: string;
    fileMismatchTitle: string;
    fileMismatchDesc: string;
    documentDetailsLabel: string;
    documentField: string;
    orderField: string;
    languagesField: string;
    pagesField: string;
    verifiedByField: string;
    verifiedOnField: string;
    verifyIntegrityLabel: string;
    computingHash: string;
    dropzoneTitlePurged: string;
    dropzoneTitle: string;
    dropzoneHintPurged: string;
    dropzoneHint: string;
    fileNotAvailableBox: string;
    disclaimerPurged: string;
    disclaimerWithHash: string;
    disclaimerNoHash: string;
    saveBtn: string;
    verificationResultLabel: string;
    exactMatchTitle: string;
    exactMatchDesc: string;
    certificationStatementLabel: string;
    defaultCertStatement: string;
    verifiedDisclaimer: string;
    reverifyBtn: string;
    fileMismatchResultTitle: string;
    fileMismatchResultDesc: string;
    mismatchDisclaimer: string;
    inspectedCannotConfirmTitle: string;
    inspectedCannotConfirmDesc: string;
    yourFileHashLabel: string;
    inspectedDisclaimer: string;
    tryAgainBtn: string;
    contactTranslatorBtn: string;
    mailtoSubject: string;
    mailtoBody: string;
    cryptographicDetails: string;
    verificationIdLabel: string;
    documentHashLabel: string;
    contentHashLabel: string;
    localVerificationNote: string;
    verificationIssuedAt: string;
    poweredBy: string;
    footerDisclaimer: string;
    zeroKnowledgeEncrypted: string;
    notFoundTitle: string;
    notFoundDesc: string;
    goToHome: string;
  };
};

// Translation dictionaries live in `./translations` (one module per
// locale) and are re-exported from `./translations.ts`. Importing this
// file only gives you types + constants — no locale payload lands in
// the bundle unless you explicitly `import { getTranslations } from
// '@/lib/i18n/translations'`. That's what lets public marketing routes
// keep a lean client bundle while dashboard/login still load all five
// dictionaries for the in-page language switcher.

export const LOCALE_COOKIE = 'ezdocu-locale';
export const DEFAULT_LOCALE: Locale = 'en';
